import React, { useState, useEffect } from 'react';
import {
   Box,
   Typography,
   Container,
   Paper,
   Grid,
   Button,
   CircularProgress,
   Divider,
   Chip,
   Tab,
   Tabs,
   Alert,
   Card,
   CardContent,
   CardMedia,
   IconButton,
   Tooltip,
   Stack,
   Avatar,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   ListItemSecondaryAction,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   DialogContentText,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Snackbar,
   Pagination,
   Checkbox,
   TextField,
} from '@mui/material';
import {
   Edit as EditIcon,
   Delete as DeleteIcon,
   PhotoCamera as PhotoCameraIcon,
   Event as EventIcon,
   People as PeopleIcon,
   Place as PlaceIcon,
   DateRange as DateRangeIcon,
   ArrowBack as ArrowBackIcon,
   MoreVert as MoreVertIcon,
   Share as ShareIcon,
   Public as PublicIcon,
   Lock as LockIcon,
   CheckCircle as CheckCircleIcon,
   Videocam as VideocamIcon,
   Computer as ComputerIcon,
   Password as PasswordIcon,
   Link as LinkIcon,
   AutoAwesome as AutoAwesomeIcon,
   Style as StyleIcon,
   Collections as CollectionsIcon,
   Close as CloseIcon,
   LocationOn as LocationOnIcon,
   Download as DownloadIcon,
   QrCode as QrCodeIcon,
   Person as PersonIcon,
   Message as MessageIcon,
   Send as SendIcon,
   ArrowBack as BackIconMsg,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
   useResponsive,
   useResponsiveTypography,
   useResponsiveSpacing,
} from '../../utils/responsive';
import { useEvents } from '../../contexts/EventContext';
import { useUser } from '../../contexts/UserContext';
import { getDefaultBackgroundStyle } from '../../utils/defaultImages';
import { Event, EventType, EventParticipant, Photo } from '../../types/Event';
import ContactsIcon from '@mui/icons-material/Contacts';
import CheckIcon from '@mui/icons-material/Check';
import { format } from 'date-fns';
import PhotoGalleryPopup from '../../components/Photos/PhotoGalleryPopup';
import {
   getDocs,
   getDoc,
   query,
   where,
   orderBy,
   collection,
   doc,
   updateDoc,
   arrayRemove,
   addDoc,
   Timestamp,
   deleteDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import EventPhotoUploader from '../../components/PhotoUpload/EventPhotoUploader';
import SendEventInvitationDialog from '../../components/invitations/SendEventInvitationDialog';
import AddToNetworkButton from '../../components/network/AddToNetworkButton';
import EventAnalytics from './components/EventAnalytics';
import { getNetworkRequests } from '../../services/networkService';
import ParticipantProfileModal from '../../components/ParticipantProfileModal';
// import SubscribeButton from '../../components/SubscribeButton'; // Hidden - not needed
import DOMPurify from 'dompurify';
import RecordingsList from '../../components/VirtualEvent/RecordingsList';
import AIGallery from '../../components/EventPhotos/AIGallery';
import { getStorage, ref, deleteObject, getDownloadURL, uploadBytes } from 'firebase/storage';
import StyleTransformModal from '../../components/PhotoTransform/StyleTransformModal';
import EditPhotoDialog from '../../components/Photos/EditPhotoDialog';
import EventQRCode from '../../components/QRCode/EventQRCode';
import { get_event_photos, get_transformed_photos_by_photo_id } from '../../services';
import { convertToJPEG } from '../../utils/imageUtils';
import PhotoLikeButton from '../../components/common/PhotoLikeButton';
import { VideoMediaBadge } from '../../components/public-gallery/VideoMediaBadge';
import { MediaUploaderAttribution, hasUploaderMediaIds } from '../../components/common/MediaUploaderAttribution';

import { Carousel } from 'react-material-ui-carousel/dist/components/Carousel';
import { ArrowBackIos, ArrowForwardIos, Save as SaveIcon } from '@mui/icons-material';
import LinkedAlbums from '../../components/albums/LinkedAlbums';
import SharePhotosButton from '../../components/PhotoSharing/SharePhotosButton';
import { useCanSharePhotos, EntityData } from '../../utils/photoSharingPermissions';
import ParticipantReminderModal from '../../components/albums/ParticipantReminderModal';
import EventFlyerGenerator from '../../components/events/EventFlyerGenerator';
import { getAuthHeaders } from '../../utils/auth';
import {
   PhotoLibrary as PhotoLibraryIcon,
   Phone as PhoneIcon,
   Language as LanguageIcon,
   Business as BusinessIcon,
} from '@mui/icons-material';
import type { PhotographerProfilePublic } from '../../components/public-gallery/types';
import { PhotographerBadge } from '../../components/common/PhotographerBadge';

// TabPanel component
interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

// Event type to display name mapping
const eventTypeNames: Record<EventType, string> = {
   social: 'Social Gathering',
   family: 'Family Event',
   cultural: 'Cultural Event',
   sports: 'Sports Event',
   business: 'Business Event',
   other: 'Other Event',
};

// Event type to color mapping
const eventTypeColors: Record<EventType, string> = {
   social: '#FF5722',
   family: '#4CAF50',
   cultural: '#9C27B0',
   sports: '#2196F3',
   business: '#607D8B',
   other: '#795548',
};

// Design tokens from reference (check.html)
const INK = '#080a0f';
const INK2 = '#0e1118';
const INK3 = '#141820';
const TEAL = '#00c4a7';
const BORDER = 'rgba(255,255,255,0.07)';
const BORDER2 = 'rgba(255,255,255,0.13)';
const W60 = 'rgba(255,255,255,0.6)';
const W35 = 'rgba(255,255,255,0.35)';

// Add this near the top of the file, with other constants
const DEFAULT_EVENT_IMAGE = 'https://via.placeholder.com/800x400?text=Event+Image';

// Helper function to get location display name
const getLocationDisplayName = (location: any): string => {
   if (!location) return 'Location TBD';
   if (typeof location === 'string') return location;
   if (typeof location === 'object') {
      // Handle EventLocation object structure
      return location.name || location.address || 'Location TBD';
   }
   return 'Location TBD';
};

// Helper function to get location address
const getLocationAddress = (location: any): string | undefined => {
   if (!location || typeof location === 'string') return undefined;
   if (typeof location === 'object') {
      return location.address || undefined;
   }
   return undefined;
};

// Add this function before the component
const sanitizeHtml = (html: string): string => {
   return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
         'p',
         'br',
         'strong',
         'em',
         'u',
         'h1',
         'h2',
         'h3',
         'ul',
         'ol',
         'li',
         'blockquote',
         'a',
         'span',
      ],
      ALLOWED_ATTR: ['href', 'style'],
   });
};

const EventDetailPage: React.FC = () => {
   const { eventId } = useParams<{ eventId: string }>();
   const navigate = useNavigate();
   const { getEvent, deleteEvent } = useEvents();
   const { currentUser } = useUser();

   // Responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

   const [event, setEvent] = useState<Event | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [tabValue, setTabValue] = useState(0);

   const [showUploader, setShowUploader] = useState(false);
   const [eventPhotos, setEventPhotos] = useState<any[]>([]);
   const [loadingPhotos, setLoadingPhotos] = useState(false);

   const [isEventOwner, setIsEventOwner] = useState(false);
   const [isEventParticipant, setIsEventParticipant] = useState(false);
   const [userRole, setUserRole] = useState<string | null>(null);

   const [networkRequests, setNetworkRequests] = useState<any[]>([]);
   const [loadingNetworkRequests, setLoadingNetworkRequests] = useState(false);

   const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
   const [photoToDelete, setPhotoToDelete] = useState<any>(null);

   const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [exporting, setExporting] = useState(false);

   const [selectedPhoto, setSelectedPhoto] = useState<any | null>(null);
   const [showEditDialog, setShowEditDialog] = useState(false);
   const [isEnhancing, setIsEnhancing] = useState(false);
   const [styleModalOpen, setStyleModalOpen] = useState(false);
   const [waitingList, setWaitingList] = useState<any[]>([]);
   const [loadingWaitlist, setLoadingWaitlist] = useState(false);
   const [approvingUsers, setApprovingUsers] = useState<Set<string>>(new Set());
   const [rejectingUsers, setRejectingUsers] = useState<Set<string>>(new Set());
   const [openAIVersion, setOpenAIVersion] = useState(false);
   const [loadingTransformedPhotos, setLoadingTransformedPhotos] = useState(false);
   const [eventTransformedPhotos, setEventTransformedPhotos] = useState<any[]>([]);
   const [openTransformedPhotoDeleteDialog, setOpenTransformedPhotoDeleteDialog] = useState(false);
   const [transformedPhotoToDelete, setTransformedPhotoToDelete] = useState<any>(null);
   const [flyerDialogOpen, setFlyerDialogOpen] = useState(false);

   // View All Photos modal state
   const [viewAllPhotosOpen, setViewAllPhotosOpen] = useState(false);
   const [currentPage, setCurrentPage] = useState(1);
   const [photosPerPage] = useState(8); // Show 8 photos per page

   // Add contact states
   const [addingContact, setAddingContact] = useState<string>(''); // Store participant ID being added
   const [addedContacts, setAddedContacts] = useState<Set<string>>(new Set()); // Track added contacts

   // Participant profile modal states
   const [participantProfileModalOpen, setParticipantProfileModalOpen] = useState(false);
   const [selectedParticipant, setSelectedParticipant] = useState<EventParticipant | null>(null);

   // Prepare entity data for permission checks
   const entityData: EntityData | null =
      event && event.id
         ? {
              id: event.id,
              createdBy: (event as any).createdBy,
              organizerId: undefined, // Events don't have organizerId, they use userId
              userId: event.userId, // This is the event owner
              members: (event as any).members,
              participants: event.participants?.map((p) => ({
                 userId: p.userId || undefined,
                 email: p.email,
                 role: p.role as any,
                 status: p.status,
              })),
           }
         : null;

   // Check if current user can share photos
   const canShare = useCanSharePhotos(entityData);

   // Photo selection state
   const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());

   // Photo selection functions
   const togglePhotoSelection = (photoId: string) => {
      setSelectedPhotos((prev) => {
         const newSelection = new Set(prev);
         if (newSelection.has(photoId)) {
            newSelection.delete(photoId);
         } else {
            newSelection.add(photoId);
         }
         return newSelection;
      });
   };

   const selectAllPhotos = () => {
      if (selectedPhotos.size === currentPhotos.length) {
         setSelectedPhotos(new Set());
      } else {
         setSelectedPhotos(new Set(currentPhotos.map((photo: any) => photo.id)));
      }
   };

   // Photo gallery popup states
   const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
   const [photoGalleryIndex, setPhotoGalleryIndex] = useState(0);

   // Add photo to event album (from gallery popup)
   const [addToAlbumDialogOpen, setAddToAlbumDialogOpen] = useState(false);
   const [addToAlbumPhoto, setAddToAlbumPhoto] = useState<any>(null);
   const [eventAlbumsList, setEventAlbumsList] = useState<{ id: string; title: string; photoCount?: number }[]>([]);
   const [loadingEventAlbums, setLoadingEventAlbums] = useState(false);
   const [selectedAlbumIdForAdd, setSelectedAlbumIdForAdd] = useState<string>('');
   const [addingPhotoToAlbum, setAddingPhotoToAlbum] = useState(false);
   const [albumsRefreshTrigger, setAlbumsRefreshTrigger] = useState(0);

   // Participant management states
   const [editParticipantDialog, setEditParticipantDialog] = useState(false);
   const [participantToEdit, setParticipantToEdit] = useState<EventParticipant | null>(null);
   const [deleteParticipantDialog, setDeleteParticipantDialog] = useState(false);
   const [participantToDelete, setParticipantToDelete] = useState<EventParticipant | null>(null);
   const [newRole, setNewRole] = useState<'owner' | 'editor' | 'viewer'>('viewer');

   // Participant reminder states
   const [reminderModalOpen, setReminderModalOpen] = useState(false);
   const [selectedPhotosForReminder, setSelectedPhotosForReminder] = useState<Set<string>>(
      new Set()
   );
   const [reminderMessage, setReminderMessage] = useState('');
   const [sendingReminder, setSendingReminder] = useState(false);

   // Inline profile popover (uploader/photographer profile on avatar click)
   const [profilePopoverUserId, setProfilePopoverUserId] = useState<string | null>(null);
   const [profilePopoverData, setProfilePopoverData] = useState<{
      displayName?: string;
      photoURL?: string;
      accountType?: string[];
      photographerProfile?: PhotographerProfilePublic | null;
      portfolioPhotos?: { id: string; url: string; thumbnailUrl?: string; title?: string }[];
   } | null>(null);
   const [profilePopoverLoading, setProfilePopoverLoading] = useState(false);
   const [profilePopoverMessageMode, setProfilePopoverMessageMode] = useState(false);
   const [profilePopoverMessageText, setProfilePopoverMessageText] = useState('');
   const [profilePopoverSenderEmail, setProfilePopoverSenderEmail] = useState('');
   const [profilePopoverSenderName, setProfilePopoverSenderName] = useState('');
   const [profilePopoverSending, setProfilePopoverSending] = useState(false);
   const [profilePopoverMessageError, setProfilePopoverMessageError] = useState<string | null>(null);

   // Pagination calculations
   const totalPages = Math.ceil(eventPhotos.length / photosPerPage);
   const startIndex = (currentPage - 1) * photosPerPage;
   const endIndex = startIndex + photosPerPage;
   const currentPhotos = eventPhotos.slice(startIndex, endIndex);

   // Pagination handlers
   const handleNextPage = () => {
      if (currentPage < totalPages) {
         setCurrentPage((prev) => prev + 1);
      }
   };

   const handlePrevPage = () => {
      if (currentPage > 1) {
         setCurrentPage((prev) => prev - 1);
      }
   };

   const handlePageChange = (page: number) => {
      setCurrentPage(page);
   };

   const handleCloseViewAllPhotos = () => {
      setViewAllPhotosOpen(false);
      setCurrentPage(1); // Reset to first page when closing
   };

   const handleCloseEditDialog = (photo: any) => {
      setShowEditDialog(true);
      setSelectedPhoto(photo);
   };

   const fetchNetworkRequests = async () => {
      if (!currentUser) return;

      try {
         setLoadingNetworkRequests(true);
         const requests = await getNetworkRequests(currentUser.uid);
         setNetworkRequests(requests);
      } catch (error) {
      } finally {
         setLoadingNetworkRequests(false);
      }
   };

   const handleSave = async (photo: any) => {
      if (!currentUser?.uid) {
         return;
      }

      try {
         if (!photo) {
            setSnackbarSeverity('error');
            setSnackbarMessage('Photo not found');
            setSnackbarOpen(true);
            return;
         }

         if (photo?.id) {
            const photoRef = doc(db, 'photos', photo?.id || '');
            const photoDoc = await getDoc(photoRef);
            if (photoDoc.exists()) {
               setSnackbarSeverity('error');
               setSnackbarMessage('Photo already exists');
               setSnackbarOpen(true);
               return;
            }
         }

         const { blob, extension } = await convertToJPEG(photo?.transformedUrl);

         // Generate unique filename for storage
         const storageFileName = `transformed_${Date.now()}_${Math.random().toString(36).substring(2)}`;
         const path = `transformed_photos/${currentUser?.uid}/${storageFileName}.${extension}`;

         // Upload to Firebase Storage
         const storage = getStorage();
         const storageRef = ref(storage, path);

         // Upload file
         await uploadBytes(storageRef, blob);

         const photoUrl = await getDownloadURL(storageRef);

         const newPhoto = {
            url: photoUrl,
            title: `Enhanced - ${selectedPhoto?.title || 'Photo'}`,
            description: `AI enhanced version of ${selectedPhoto?.title || 'photo'}`,
            fileName: `${storageFileName}.${extension}`,
            size: blob.size,
            mimeType: blob.type,
            isTransformed: true,
            userId: currentUser?.uid,
            uploadedBy: currentUser?.uid,
            type: 'transformed_photo',
            basePhotoId: photo.basePhotoId || selectedPhoto?.id || '',
            createdAt: Timestamp.now(),
            uploadedAt: Timestamp.now(),
            takenAt: selectedPhoto?.takenAt
               ? Timestamp.fromDate(new Date(selectedPhoto.takenAt))
               : Timestamp.now(),
            eventId: event?.id || '',
            tags: [],
            likeCount: 0,
            likedBy: [],
         };

         const photoRef = await addDoc(collection(db, 'photos'), newPhoto);

         setSnackbarSeverity('success');
         setSnackbarMessage('Photo saved successfully');
         setSnackbarOpen(true);
      } catch (err) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to upload photo');
         setSnackbarOpen(true);
      } finally {
      }
   };

   const handleDeleteTransformedPhotoConfirm = async () => {
      try {
         if (!transformedPhotoToDelete) return;
         const photoRef = doc(db, 'photos', transformedPhotoToDelete?.id || '');
         await deleteDoc(photoRef);
         setEventTransformedPhotos((prev: any[]): any[] => [
            ...prev.filter(
               (p: any) =>
                  p.id != transformedPhotoToDelete?.id || p.url != transformedPhotoToDelete?.url
            ),
         ]);

         setSnackbarSeverity('success');
         setSnackbarMessage('Photo deleted successfully');
         setSnackbarOpen(true);
      } catch (err) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to delete photo');
         setSnackbarOpen(true);
      } finally {
         setOpenTransformedPhotoDeleteDialog(false);
         setTransformedPhotoToDelete(null);
      }
   };

   const onPhotoEnhanced = async (images: any[], photoId: string) => {
      let totalFails = 0;
      let totalSuccess = 0;
      let totalSaved = 0;
      setIsEnhancing(true);
      const photos = images.map((image: any) => {
         return {
            ...image,
            transformedUrl: image?.url,
            basePhotoId: photoId,
         };
      });

      try {
         await Promise.all(
            photos.map(async (photo: any) => {
               try {
                  await handleSave(photo);
                  totalSuccess++;
               } catch (err) {
                  totalFails++;
               }
            })
         );

         setSnackbarSeverity('success');
         setSnackbarMessage(
            `${totalSuccess} photos saved successfully, ${totalFails} photos failed`
         );
         setSnackbarOpen(true);
      } catch (err) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to enhance photo');
         setSnackbarOpen(true);
      } finally {
         setIsEnhancing(false);
         setSelectedPhoto(null);
      }
   };

   const fetchEventDetails = async () => {
      try {
         setLoading(true);
         setError(null);

         if (!eventId) {
            setError('Event ID is required');
            return;
         }

         const eventData = await getEvent(eventId);

         if (!eventData) {
            setError('Event not found');
            return;
         }

         // Ensure both members and participants arrays exist as modifiable arrays
         let updatedEventData = {
            ...eventData,
            participants: Array.isArray(eventData.participants) ? [...eventData.participants] : [],
            members: Array.isArray(eventData.members) ? [...eventData.members] : [],
         };

         // Check relationship status for each member if current user exists
         if (currentUser && updatedEventData.members.length > 0) {
            try {
               // Get all relationships for the current user (both directions)
               const relationshipNetworkRef = collection(db, 'relationship_network');

               // Query 1: Where current user is the initiator
               const q1 = query(
                  relationshipNetworkRef,
                  where('userId', '==', currentUser.uid),
                  where('status', '==', 'accepted')
               );

               // Query 2: Where current user is the recipient (reverse relationships)
               const q2 = query(
                  relationshipNetworkRef,
                  where('relationToUserId', '==', currentUser.uid),
                  where('status', '==', 'accepted')
               );

               const [querySnapshot1, querySnapshot2] = await Promise.all([
                  getDocs(q1),
                  getDocs(q2),
               ]);

               const relationships1 = querySnapshot1.docs.map((doc) => doc.data());
               const relationships2 = querySnapshot2.docs.map((doc) => doc.data());
               const allRelationships = [...relationships1, ...relationships2];

               // Update members with relationship status
               updatedEventData.members = updatedEventData.members.map(
                  (member: EventParticipant) => {
                     // Check if this member is in the current user's network (both directions)
                     const isInNetwork = allRelationships.some((rel) => {
                        // Check forward relationship (current user -> member)
                        const forwardMatch =
                           rel.userId === currentUser.uid &&
                           (rel.relationToUserId === member.userId ||
                              rel.relationToEmail === member.email);

                        // Check reverse relationship (member -> current user)
                        const reverseMatch =
                           rel.relationToUserId === currentUser.uid &&
                           (rel.userId === member.userId || rel.recipientEmail === member.email);

                        return forwardMatch || reverseMatch;
                     });

                     return {
                        ...member,
                        isInNetwork: isInNetwork,
                     };
                  }
               );
            } catch (relationshipError) {
               console.error('Error fetching relationship status:', relationshipError);
               // Continue without relationship status if there's an error
            }
         }

         // Fetch user profiles for member avatars
         if (updatedEventData.members.length > 0) {
            try {
               const userIds = updatedEventData.members
                  .map((member) => member.userId)
                  .filter((id): id is string => id !== null && id !== undefined);

               if (userIds.length > 0) {
                  const userProfiles: { [userId: string]: any } = {};

                  for (const userId of userIds) {
                     try {
                        // Try userProfiles first (where complete profile data is stored)
                        let userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));

                        if (!userProfileDoc.exists()) {
                           // Fallback to userProfile collection
                           userProfileDoc = await getDoc(doc(db, 'userProfile', userId));
                        }

                        if (!userProfileDoc.exists()) {
                           // Fallback to users collection
                           userProfileDoc = await getDoc(doc(db, 'users', userId));
                        }

                        if (userProfileDoc.exists()) {
                           userProfiles[userId] = userProfileDoc.data();
                        }
                     } catch (error) {
                        console.error(`Error fetching profile for user ${userId}:`, error);
                     }
                  }

                  // Add profile data to members
                  updatedEventData.members = updatedEventData.members.map((member) => ({
                     ...member,
                     profileData: member.userId ? userProfiles[member.userId] || null : null,
                  }));
               }
            } catch (profileError) {
               console.error('Error fetching user profiles:', profileError);
               // Continue without profile data if there's an error
            }
         }

         setEvent(updatedEventData);
         setIsEventOwner(updatedEventData.userId == currentUser?.uid);
      } catch (err) {
         setError('Failed to load event details. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   const loadEventPhotos = async () => {
      try {
         setLoadingPhotos(true);

         // Early return if no event ID or currentUser
         if (!event?.id || !currentUser?.uid) {
            console.log('Missing event ID or currentUser, skipping photo load');
            setEventPhotos([]);
            return;
         }

         const data = await get_event_photos(event.id, currentUser.uid);
         console.log('Event photos data received:', data);

         if (data && Array.isArray(data)) {
            const processedPhotos = data.map((photo: any) => {
               console.log('Processing photo:', photo);
               return {
                  ...photo,
                  id: photo.id,
                  url: photo.url || photo.thumbnailUrl || '',
                  title: photo?.title || 'Untitled',
                  description: photo?.description || '',
                  likeCount: photo.likeCount || 0,
                  isLiked: photo.isLiked || false,
                  takenAt: photo?.takenAt
                     ? photo.takenAt.toDate
                        ? photo.takenAt.toDate()
                        : new Date(photo.takenAt)
                     : null,
                  uploadedAt: photo?.uploadedAt
                     ? photo.uploadedAt.toDate
                        ? photo.uploadedAt.toDate()
                        : new Date(photo.uploadedAt)
                     : null,
                  createdAt: photo?.createdAt
                     ? photo.createdAt.toDate
                        ? photo.createdAt.toDate()
                        : new Date(photo.createdAt)
                     : null,
                  location: photo?.location || null,
               };
            });

            setEventPhotos(processedPhotos);
         } else {
            console.log('No event photos data or invalid format:', data);
            setEventPhotos([]);
         }
      } catch (err) {
         console.error('Error loading event photos:', err);
         setEventPhotos([]);
      } finally {
         setLoadingPhotos(false);
      }
   };

   const handleUploadComplete = () => {
      setShowUploader(false);
      loadEventPhotos();
   };

   // Fetch uploader/photographer profile when avatar is clicked
   useEffect(() => {
      if (!profilePopoverUserId) {
         setProfilePopoverData(null);
         return;
      }
      let cancelled = false;
      setProfilePopoverLoading(true);
      setProfilePopoverData(null);
      fetch(`${process.env.REACT_APP_API_URL}/api/profile/public/${profilePopoverUserId}`)
         .then((res) => (res.ok ? res.json() : null))
         .then((data) => {
            if (!cancelled && data) setProfilePopoverData(data);
         })
         .catch(() => {
            if (!cancelled) setProfilePopoverData(null);
         })
         .finally(() => {
            if (!cancelled) setProfilePopoverLoading(false);
         });
      return () => {
         cancelled = true;
      };
   }, [profilePopoverUserId]);

   const handleTabChange = (syntheticEvent: React.SyntheticEvent, newValue: number) => {
      // Simply set the tab value - let the conditional rendering handle what gets displayed
      setTabValue(newValue);
   };

   // Function to handle editing participant role
   const handleEditParticipant = (participant: EventParticipant) => {
      setParticipantToEdit(participant);
      setNewRole(participant.role || 'viewer');
      setEditParticipantDialog(true);
   };

   // Function to handle deleting participant
   const handleDeleteParticipant = (participant: EventParticipant) => {
      setParticipantToDelete(participant);
      setDeleteParticipantDialog(true);
   };

   // Function to confirm role change
   const confirmRoleChange = async () => {
      if (!participantToEdit || !event || !event.id) return;

      try {
         const eventRef = doc(db, 'events', event.id);
         const eventDoc = await getDoc(eventRef);

         if (eventDoc.exists()) {
            const eventData = eventDoc.data();
            const members = eventData.members || [];

            const updatedMembers = members.map((member: EventParticipant) =>
               member.userId === participantToEdit.userId ||
               member.email === participantToEdit.email
                  ? { ...member, role: newRole }
                  : member
            );

            await updateDoc(eventRef, { members: updatedMembers });

            // Update local state
            setEvent((prev) =>
               prev
                  ? {
                       ...prev,
                       members: updatedMembers,
                    }
                  : null
            );

            setSnackbarMessage('Participant role updated successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
         }
      } catch (error) {
         console.error('Error updating participant role:', error);
         setSnackbarMessage('Failed to update participant role');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }

      setEditParticipantDialog(false);
      setParticipantToEdit(null);
   };

   // Function to confirm participant deletion
   const confirmParticipantDeletion = async () => {
      if (!participantToDelete || !event || !event.id) return;

      try {
         const eventRef = doc(db, 'events', event.id);
         const eventDoc = await getDoc(eventRef);

         if (eventDoc.exists()) {
            const eventData = eventDoc.data();
            const members = eventData.members || [];

            const updatedMembers = members.filter(
               (member: EventParticipant) =>
                  member.userId !== participantToDelete.userId &&
                  member.email !== participantToDelete.email
            );

            await updateDoc(eventRef, { members: updatedMembers });

            // Update local state
            setEvent((prev) =>
               prev
                  ? {
                       ...prev,
                       members: updatedMembers,
                    }
                  : null
            );

            setSnackbarMessage('Participant removed successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
         }
      } catch (error) {
         console.error('Error removing participant:', error);
         setSnackbarMessage('Failed to remove participant');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }

      setDeleteParticipantDialog(false);
      setParticipantToDelete(null);
   };

   // Function to handle opening participant profile modal
   const handleOpenParticipantProfile = (participant: EventParticipant) => {
      setSelectedParticipant(participant);
      setParticipantProfileModalOpen(true);
   };

   // Function to handle closing participant profile modal
   const handleCloseParticipantProfile = () => {
      setParticipantProfileModalOpen(false);
      setSelectedParticipant(null);
   };

   // Reminder modal handlers
   const handleOpenReminderModal = () => {
      setReminderModalOpen(true);
   };

   const handleCloseReminderModal = () => {
      setReminderModalOpen(false);
      setSelectedPhotosForReminder(new Set());
      setReminderMessage('');
   };

   const handleTogglePhotoForReminder = (photoId: string) => {
      setSelectedPhotosForReminder((prev) => {
         const newSet = new Set(prev);
         if (newSet.has(photoId)) {
            newSet.delete(photoId);
         } else {
            if (newSet.size >= 5) {
               setSnackbarMessage('You can select a maximum of 5 photos');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
               return prev;
            }
            newSet.add(photoId);
         }
         return newSet;
      });
   };

   const handleSendReminder = async (selectedParticipantIds: string[]) => {
      try {
         if (selectedParticipantIds.length === 0) {
            setSnackbarMessage('Please select at least one participant');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
         }

         setSendingReminder(true);

         // Get members from event
         const members = event?.members || event?.participants || [];
         const selectedParticipantsList = members.filter((m: EventParticipant) =>
            selectedParticipantIds.includes(m.userId || m.email)
         );

         const recipients = selectedParticipantsList.map((m: EventParticipant) => ({
            userId: m.userId,
            email: m.email,
         }));

      const selectedPhotosData = eventPhotos.filter((p) =>
         selectedPhotosForReminder.has(p.id)
      );

      const authHeaders = await getAuthHeaders();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/events/send-participant-reminder`, {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
         },
         body: JSON.stringify({
            eventId: event?.id,
            eventTitle: event?.title,
            recipients,
            message: reminderMessage,
            photos: selectedPhotosData.map((p) => ({
               id: p.id,
               url: p.url,
               title: p.title || 'Untitled',
            })),
         }),
      });

      if (!response.ok) {
         const errorData = await response.json().catch(() => ({ message: 'Failed to send reminders' }));
         throw new Error(errorData.message || 'Failed to send reminders');
      }

      const result = await response.json();

      setSnackbarMessage(
         `Sending reminders to ${recipients.length} participant(s)... This may take a moment.`
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseReminderModal();
   } catch (error: any) {
      console.error('Error sending reminder:', error);
      setSnackbarMessage(error.message || 'Failed to send reminder. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
   } finally {
      setSendingReminder(false);
   }
   };

   // Function to add participant to relationship network
   const addToContacts = async (participant: EventParticipant) => {
      if (!currentUser || addingContact === participant.userId || !participant.email) return;

      setAddingContact(participant.userId || participant.email);

      try {
         // Check if relationship already exists (both directions)
         const relationshipNetworkRef = collection(db, 'relationship_network');

         // Query 1: Where current user is the initiator
         const q1 = query(
            relationshipNetworkRef,
            where('userId', '==', currentUser.uid),
            where('relationToEmail', '==', participant.email),
            where('status', '==', 'accepted')
         );

         // Query 2: Where current user is the recipient
         const q2 = query(
            relationshipNetworkRef,
            where('relationToUserId', '==', currentUser.uid),
            where('recipientEmail', '==', participant.email),
            where('status', '==', 'accepted')
         );

         const [querySnapshot1, querySnapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);

         if (!querySnapshot1.empty || !querySnapshot2.empty) {
            setSnackbarMessage('This person is already in your contacts');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
         }

         // Create new relationship
         const relationshipData = {
            userId: currentUser.uid,
            relationToUserId: participant.userId || null,
            relationToEmail: participant.email,
            relationType: 'friend', // Default relationship type
            relationLabel: 'Event Participant',
            notes: `Connected through event: ${event?.title || 'Event'}`,
            status: 'accepted',
            isNetworkConfirmed: true,
            source: 'event_participant',
            sourceId: event?.id,
            createdAt: new Date(),
            updatedAt: new Date(),
            // Store contact details for better display
            recipientDisplayName: participant.displayName || participant.email.split('@')[0],
            recipientEmail: participant.email,
            recipientPhotoURL: participant.photoURL || '',
         };

         await addDoc(relationshipNetworkRef, relationshipData);

         // Update UI state
         setAddedContacts(
            (prev) => new Set([...Array.from(prev), participant.userId || participant.email])
         );

         // Update the event member's isInNetwork status
         setEvent((prevEvent) => {
            if (!prevEvent || !prevEvent.members) return prevEvent;

            const updatedMembers = prevEvent.members.map((member: EventParticipant) => {
               if (member.userId === participant.userId || member.email === participant.email) {
                  return { ...member, isInNetwork: true };
               }
               return member;
            });

            return { ...prevEvent, members: updatedMembers };
         });

         setSnackbarMessage(
            `${participant.displayName || participant.email} added to your contacts!`
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error adding contact:', error);
         setSnackbarMessage('Failed to add contact. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setAddingContact('');
      }
   };

   const handleEdit = () => {
      navigate(`/events/${eventId}/edit`);
   };

   const handleExport = async () => {
      if (exporting || !eventId) return;
      setExporting(true);
      try {
         const headers = await getAuthHeaders();
         const res = await fetch(
            `${process.env.REACT_APP_API_URL}/api/events/${eventId}/export`,
            { headers }
         );
         if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new Error(body?.message || 'Export failed');
         }
         const blob = await res.blob();
         const url = window.URL.createObjectURL(blob);
         const a = document.createElement('a');
         a.href = url;
         const disposition = res.headers.get('content-disposition') || '';
         const match = disposition.match(/filename="?([^"]+)"?/);
         a.download = match?.[1] || 'event-export.xlsx';
         document.body.appendChild(a);
         a.click();
         a.remove();
         window.URL.revokeObjectURL(url);
         setSnackbarSeverity('success');
         setSnackbarMessage('Event export downloaded');
         setSnackbarOpen(true);
      } catch (err: any) {
         setSnackbarSeverity('error');
         setSnackbarMessage(err?.message || 'Export failed');
         setSnackbarOpen(true);
      } finally {
         setExporting(false);
      }
   };

   const formatEventDate = (date: any) => {
      try {
         if (!date) return 'Invalid date';

         // Handle different date formats
         let dateObj: Date;

         if (date instanceof Date) {
            dateObj = date;
         } else if (typeof date === 'string') {
            dateObj = new Date(date);
         } else if (date.seconds) {
            // Firestore Timestamp
            dateObj = new Date(date.seconds * 1000);
         } else if (date.toDate) {
            // Firestore Timestamp with toDate method
            dateObj = date.toDate();
         } else {
            dateObj = new Date(date);
         }

         // Check if the date is valid
         if (isNaN(dateObj.getTime())) {
            return 'Invalid date';
         }

         return dateObj.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
         });
      } catch (err) {
         console.error('Error formatting date:', err);
         return 'Invalid date';
      }
   };

   const handleJoinMeeting = () => {
      if (!event) return;

      if (event.isVirtual && event.virtualRoomSid) {
         navigate(`/join-room/${event.virtualRoomSid}?eventId=${eventId}`);
      }
   };

   const handleDeletePhoto = (photo: any) => {
      setPhotoToDelete(photo);
      setOpenDeleteDialog(true);
   };

   const handleTransformedPhotoDelete = (photo: any) => {
      setTransformedPhotoToDelete(photo);
      setOpenTransformedPhotoDeleteDialog(true);
   };

   const handleDeleteCancel = () => {
      setOpenDeleteDialog(false);
      setPhotoToDelete(null);
   };

   const handleDeleteConfirm = async () => {
      if (!photoToDelete) return;

      try {
         await deleteDoc(doc(db, 'photos', photoToDelete.id));

         // Update local state
         setEventPhotos((prev) => prev.filter((p) => p.id !== photoToDelete.id));
         setOpenDeleteDialog(false);
         setPhotoToDelete(null);

         // Show success message
         setSnackbarMessage('Photo deleted successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (err) {
         setSnackbarMessage('Failed to delete photo. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   const handleOpenAddToAlbum = (photo: any) => {
      setAddToAlbumPhoto(photo);
      setSelectedAlbumIdForAdd('');
      setAddToAlbumDialogOpen(true);
   };

   const handleCloseAddToAlbumDialog = () => {
      setAddToAlbumDialogOpen(false);
      setAddToAlbumPhoto(null);
      setSelectedAlbumIdForAdd('');
      setEventAlbumsList([]);
   };

   const handleAddPhotoToAlbumConfirm = async () => {
      if (!addToAlbumPhoto?.id || !selectedAlbumIdForAdd || !eventId) return;
      setAddingPhotoToAlbum(true);
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${selectedAlbumIdForAdd}/add-photos`,
            {
               method: 'POST',
               headers: { 'Content-Type': 'application/json', ...headers },
               body: JSON.stringify({ photoIds: [addToAlbumPhoto.id] }),
            }
         );
         if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.message || 'Failed to add photo to album');
         }
         setSnackbarMessage('Photo added to album');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         setAlbumsRefreshTrigger((k) => k + 1);
         handleCloseAddToAlbumDialog();
      } catch (err) {
         setSnackbarMessage(err instanceof Error ? err.message : 'Failed to add photo to album');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setAddingPhotoToAlbum(false);
      }
   };

   const fetchTransformedPhotos = async () => {
      try {
         setLoadingTransformedPhotos(true);
         const transformedPhotos = await get_transformed_photos_by_photo_id(selectedPhoto.id || '');
         setEventTransformedPhotos(transformedPhotos);
      } catch (err) {
      } finally {
         setLoadingTransformedPhotos(false);
      }
   };

   useEffect(() => {
      fetchEventDetails();
   }, [eventId]);

   useEffect(() => {
      if (!addToAlbumDialogOpen || !eventId) return;
      let cancelled = false;
      setLoadingEventAlbums(true);
      (async () => {
         try {
            const headers = await getAuthHeaders();
            const res = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/${eventId}/albums`,
               { headers }
            );
            if (cancelled) return;
            if (!res.ok) {
               setEventAlbumsList([]);
               return;
            }
            const data = await res.json();
            const list = Array.isArray(data) ? data : data.albums || data.data || [];
            setEventAlbumsList(
               list.map((a: any) => ({
                  id: a.id,
                  title: a.title || 'Untitled',
                  photoCount: a.photoCount ?? a.photoIds?.length,
               }))
            );
         } catch {
            if (!cancelled) setEventAlbumsList([]);
         } finally {
            if (!cancelled) setLoadingEventAlbums(false);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [addToAlbumDialogOpen, eventId]);

   useEffect(() => {
      if (event?.id && currentUser) {
         loadEventPhotos();
      }
   }, [
      event?.id,
      currentUser,
      showEditDialog,
      isEnhancing,
      styleModalOpen,
      tabValue,
      openTransformedPhotoDeleteDialog,
      openDeleteDialog,
   ]);

   useEffect(() => {
      if (currentUser && event) {
         // Use only the new members array structure
         const members = event.members || [];

         // Check if the current user is the event owner or editor (both have owner-level access)
         const isOwner = members.some(
            (p: EventParticipant) =>
               (p.role === 'owner' || p.role === 'editor') && p.userId === currentUser.uid
         );
         setIsEventOwner(isOwner);

         // Check if the current user is an accepted participant
         const isParticipant = members.some(
            (p: EventParticipant) =>
               p.userId === currentUser.uid && (p.status === 'accepted' || !p.status)
         );
         setIsEventParticipant(isParticipant);

         // Determine user's role in the event
         const currentUserMember = members.find(
            (p: EventParticipant) => p.userId === currentUser.uid
         );
         setUserRole(currentUserMember?.role || null);
      }
   }, [event, currentUser]);

   // Separate useEffect to handle tab value resets
   useEffect(() => {
      if (event && !event.isVirtual && tabValue > 3) {
         setTabValue(0); // Reset to Details tab for non-virtual events
      }
   }, [event?.isVirtual]);

   useEffect(() => {
      if (currentUser && event) {
         fetchNetworkRequests();
      }
   }, [currentUser, event]);

   useEffect(() => {
      if (selectedPhoto && openAIVersion) {
         fetchTransformedPhotos();
      }
   }, [selectedPhoto, openAIVersion]);

   // Fetch waitlist data when event is public and user is owner
   useEffect(() => {
      console.log('Waitlist useEffect triggered:', {
         eventId,
         isPublic: event?.isPublic,
         isEventOwner,
         eventUserId: event?.userId,
         currentUserId: currentUser?.uid,
      });
      fetchWaitlist();
   }, [eventId, isEventOwner]);

   const renderEventInfo = () => {
      if (!event) return null;

      // Check if the current user is a participant with access
      const isParticipant =
         currentUser &&
         event.members?.some(
            (p: EventParticipant) => p.userId === currentUser.uid && p.status === 'accepted'
         );

      return (
         <Box sx={{ color: '#fff' }}>
            <Box sx={{ mb: 2.75 }}>
               <Typography
                  variant='h5'
                  component='h2'
                  sx={{
                     fontFamily: 'Cormorant Garamond, serif',
                     fontSize: 22,
                     fontWeight: 300,
                     color: '#fff',
                  }}
               >
               Event Details
            </Typography>
            </Box>

            <Grid container spacing={3}>
               <Grid item xs={12} md={6}>
                  <Card
                     sx={{
                        height: '100%',
                        bgcolor: INK2,
                        border: `1px solid ${BORDER}`,
                        borderRadius: 2,
                        p: 2.75,
                     }}
                  >
                     <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <Typography
                           variant='subtitle1'
                           gutterBottom
                           sx={{
                              fontFamily: 'DM Mono, monospace',
                              fontSize: 9,
                              letterSpacing: 2.5,
                              textTransform: 'uppercase',
                              color: TEAL,
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                           }}
                        >
                           <DateRangeIcon sx={{ fontSize: 16 }} />
                           Date & Time
                        </Typography>
                        <Box sx={{ ml: 4, mb: 2 }}>
                           <Typography variant='body1' sx={{ color: W60, fontSize: 13 }}>
                              <strong style={{ color: '#fff' }}>Starts:</strong> {formatEventDate(event.startDate)}
                           </Typography>
                           {event.endDate && (
                              <Typography variant='body1' sx={{ color: W60, fontSize: 13 }}>
                                 <strong style={{ color: '#fff' }}>Ends:</strong> {formatEventDate(event.endDate)}
                              </Typography>
                           )}
                        </Box>

                        <Typography
                           variant='subtitle1'
                           gutterBottom
                           sx={{
                              fontFamily: 'DM Mono, monospace',
                              fontSize: 9,
                              letterSpacing: 2.5,
                              textTransform: 'uppercase',
                              color: TEAL,
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                           }}
                        >
                           <PlaceIcon sx={{ fontSize: 16 }} />
                           Location
                        </Typography>
                        <Box sx={{ ml: 4, mb: 2 }}>
                           {event.isVirtual ? (
                              <Box>
                                 <Chip
                                    icon={<VideocamIcon />}
                                    label='Virtual Event'
                                    sx={{ mb: 1, bgcolor: 'rgba(0,196,167,0.1)', color: TEAL, border: '1px solid rgba(0,196,167,0.22)' }}
                                 />
                                 {event.location && (
                                    <Typography variant='body2' sx={{ color: W35, fontSize: 13 }}>
                                       Optional in-person location:{' '}
                                       {getLocationDisplayName(event.location)}
                                    </Typography>
                                 )}
                              </Box>
                           ) : (
                              <Box>
                                 <Typography variant='body1' sx={{ color: W60, fontSize: 13 }}>
                                    {getLocationDisplayName(event.location)}
                                 </Typography>
                                 <Typography variant='body2' sx={{ color: W35, fontSize: 13 }}>
                                    {getLocationAddress(event.location)}
                                 </Typography>
                              </Box>
                           )}
                        </Box>

                        <Typography
                           variant='subtitle1'
                           gutterBottom
                           sx={{
                              fontFamily: 'DM Mono, monospace',
                              fontSize: 9,
                              letterSpacing: 2.5,
                              textTransform: 'uppercase',
                              color: TEAL,
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                           }}
                        >
                           <PeopleIcon sx={{ fontSize: 16 }} />
                           Participants
                        </Typography>
                        <Box sx={{ ml: 4 }}>
                           {event.enableParticipantSharing ||
                           userRole === 'owner' ||
                           userRole === 'editor' ? (
                              <Typography variant='body1' sx={{ color: W60, fontSize: 13 }}>
                                 {event.members?.length || 0}{' '}
                                 {(event.members?.length || 0) === 1 ? 'person' : 'people'}{' '}
                                 participating
                              </Typography>
                           ) : (
                              <Typography variant='body1' sx={{ color: W35, fontSize: 13 }}>
                                 Participant list is private
                              </Typography>
                           )}
                        </Box>
                     </CardContent>
                  </Card>
               </Grid>

               <Grid item xs={12} md={6}>
                  <Card
                     sx={{
                        height: '100%',
                        bgcolor: INK2,
                        border: `1px solid ${BORDER}`,
                        borderRadius: 2,
                        p: 2.75,
                     }}
                  >
                     <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <Typography
                           variant='subtitle1'
                           gutterBottom
                           sx={{
                              fontFamily: 'DM Mono, monospace',
                              fontSize: 9,
                              letterSpacing: 2.5,
                              textTransform: 'uppercase',
                              color: TEAL,
                              mb: 2,
                           }}
                        >
                           Description
                        </Typography>
                        {event.description ? (
                           <Box
                              sx={{
                                 color: W60,
                                 fontSize: 14,
                                 lineHeight: 1.8,
                                 '& h1': {
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    marginBottom: '1rem',
                                    color: '#fff',
                                 },
                                 '& h2': {
                                    fontSize: '1.25rem',
                                    fontWeight: 'bold',
                                    marginBottom: '0.75rem',
                                    color: '#fff',
                                 },
                                 '& ul, & ol': {
                                    paddingLeft: '2rem',
                                    marginBottom: '1rem',
                                 },
                                 '& li': {
                                    marginBottom: '0.5rem',
                                 },
                                 '& p': {
                                    marginBottom: '1rem',
                                    lineHeight: '1.6',
                                 },
                                 '& a': {
                                    color: TEAL,
                                    textDecoration: 'none',
                                    '&:hover': {
                                       textDecoration: 'underline',
                                    },
                                 },
                              }}
                              dangerouslySetInnerHTML={{
                                 __html: sanitizeHtml(event.description),
                              }}
                           />
                        ) : (
                           <Typography variant='body1' sx={{ color: W35, fontSize: 14 }}>
                              No description provided.
                           </Typography>
                        )}
                     </CardContent>
                  </Card>
               </Grid>

               {/* Virtual event details - visible only to accepted participants */}
               {event.isVirtual && isParticipant && (
                  <Grid item xs={12}>
                     <Card
                        sx={{
                           borderLeft: 4,
                           borderColor: TEAL,
                           bgcolor: INK2,
                           border: `1px solid ${BORDER}`,
                           borderRadius: 2,
                        }}
                     >
                        <CardContent>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <VideocamIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                              <Typography variant='h6'>Virtual Event Access</Typography>
                           </Box>

                           <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                 <Typography variant='subtitle2' gutterBottom>
                                    <ComputerIcon
                                       sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }}
                                    />
                                    Platform
                                 </Typography>
                                 <Typography variant='body1' sx={{ mb: 2 }}>
                                    {event.virtualEventProvider
                                       ? event.virtualEventProvider.charAt(0).toUpperCase() +
                                         event.virtualEventProvider.slice(1)
                                       : 'Custom Platform'}
                                 </Typography>

                                 <Typography variant='subtitle2' gutterBottom>
                                    <LinkIcon
                                       sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }}
                                    />
                                    Event Link
                                 </Typography>
                                 <Box sx={{ mb: 2 }}>
                                    {isEventOwner ? (
                                       <Button
                                          variant='contained'
                                          color='primary'
                                          startIcon={<VideocamIcon />}
                                          onClick={handleJoinMeeting}
                                          sx={{ ml: 2, mb: 1 }}
                                       >
                                          Start Event as Host
                                       </Button>
                                    ) : (
                                       <Button
                                          variant='outlined'
                                          startIcon={<VideocamIcon />}
                                          onClick={handleJoinMeeting}
                                          sx={{ mb: 1 }}
                                       >
                                          Join Event
                                       </Button>
                                    )}
                                    <Typography variant='body2' color='text.secondary'>
                                       {event.virtualEventProvider === 'twilio'
                                          ? 'Access via the Virtual Event tab'
                                          : event.virtualMeetingUrl}
                                    </Typography>
                                 </Box>
                              </Grid>
                           </Grid>

                           <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                              <Typography variant='caption' color='text.secondary'>
                                 Please do not share access details with others. Only registered
                                 participants should join this virtual event.
                              </Typography>
                           </Box>
                        </CardContent>
                     </Card>
                  </Grid>
               )}

               {/* Show message for non-participants if this is a virtual event */}
               {event.isVirtual && !isParticipant && (
                  <Grid item xs={12}>
                     <Alert
                        severity='info'
                        icon={<VideocamIcon />}
                        sx={{
                           bgcolor: INK2,
                           border: `1px solid ${BORDER}`,
                           color: W60,
                        }}
                     >
                        This is a virtual event. You must be registered to access virtual event
                        details.
                     </Alert>
                  </Grid>
               )}

               {/* Map preview - placeholder for now */}
               {/* <Grid item xs={12}>
                  <Card>
                     <CardContent>
                        <Typography variant='subtitle1' gutterBottom>
                           Location Map
                        </Typography>
                        <Box
                           sx={{
                              height: 300,
                              bgcolor: 'action.hover',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                           }}
                        >
                           <Typography color='text.secondary'>
                              Map preview will be displayed here
                           </Typography>
                        </Box>
                     </CardContent>
                  </Card>
               </Grid> */}
            </Grid>
         </Box>
      );
   };

   const renderPhotos = () => {
      return (
         <Box sx={{ color: '#fff' }}>
            {/* Mobile-optimized header section */}
            {isMobile ? (
               <Box sx={{ mb: 2 }}>
                  <Typography
                     variant='h6'
                     component='h2'
                     sx={{
                        fontFamily: 'Cormorant Garamond, serif',
                        fontSize: 22,
                        fontWeight: 300,
                        color: '#fff',
                        mb: 2,
                     }}
                  >
                     Event Photos
                  </Typography>

                  {/* Mobile buttons layout */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                     {eventPhotos.length > 0 && (
                        <>
                           <Button
                              variant='outlined'
                              startIcon={<CollectionsIcon sx={{ fontSize: '16px' }} />}
                              onClick={() => setViewAllPhotosOpen(true)}
                              fullWidth
                              sx={{
                                 py: 1.5,
                                 borderRadius: '12px',
                                 textTransform: 'none',
                                 fontSize: '0.875rem',
                                 fontWeight: 500,
                                 borderColor: '#00C4A7',
                                 color: '#00C4A7',
                                 '&:hover': {
                                    borderColor: '#00C4A7',
                                    backgroundColor: 'rgba(1, 148, 154, 0.04)',
                                 },
                              }}
                           >
                              View All ({eventPhotos.length})
                           </Button>
                           {selectedPhotos.size > 0 && (
                              <>
                                 <Button
                                    variant='text'
                                    onClick={selectAllPhotos}
                                    fullWidth
                                    sx={{ fontSize: '0.75rem', py: 1 }}
                                 >
                                    {selectedPhotos.size === currentPhotos.length
                                       ? 'Deselect All'
                                       : 'Select All'}
                                 </Button>
                                 <Typography
                                    variant='body2'
                                    color='primary'
                                    sx={{ fontWeight: 'bold', textAlign: 'center', py: 1 }}
                                 >
                                    {selectedPhotos.size} photo
                                    {selectedPhotos.size !== 1 ? 's' : ''} selected
                                 </Typography>
                              </>
                           )}
                           <SharePhotosButton
                              entityType='event'
                              entityId={eventId!}
                              entityTitle={event?.title || 'Event'}
                              entityData={entityData}
                              selectedPhotos={Array.from(selectedPhotos)
                                 .map((id) => eventPhotos.find((p) => p.id === id))
                                 .filter(Boolean)}
                              variant='outlined'
                              fullWidth
                              sx={{
                                 py: 1.5,
                                 borderRadius: '12px',
                                 textTransform: 'none',
                                 fontSize: '0.875rem',
                                 fontWeight: 500,
                              }}
                           />
                        </>
                     )}
                     <Button
                        variant='contained'
                        startIcon={<PhotoCameraIcon sx={{ fontSize: '16px' }} />}
                        onClick={() => setShowUploader((prev) => !prev)}
                        fullWidth
                        sx={{
                           py: 1.5,
                           borderRadius: '12px',
                           textTransform: 'none',
                           fontSize: '0.875rem',
                           fontWeight: 600,
                           background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                           '&:hover': {
                              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                           },
                        }}
                     >
                        {showUploader ? 'Hide Uploader' : 'Add Photos'}
                     </Button>
                  </Box>
               </Box>
            ) : (
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     mb: 2,
                  }}
               >
                  <Typography
                     variant='h5'
                     component='h2'
                     sx={{
                        fontFamily: 'Cormorant Garamond, serif',
                        fontSize: 22,
                        fontWeight: 300,
                        color: '#fff',
                     }}
                  >
                     Event Photos
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                     {eventPhotos.length > 0 && (
                        <>
                           <Button
                              variant='outlined'
                              startIcon={<CollectionsIcon />}
                              onClick={() => setViewAllPhotosOpen(true)}
                           >
                              View All ({eventPhotos.length})
                           </Button>
                           {selectedPhotos.size > 0 && (
                              <>
                                 <Button
                                    variant='text'
                                    onClick={selectAllPhotos}
                                    size='small'
                                    sx={{ fontSize: '0.75rem' }}
                                 >
                                    {selectedPhotos.size === currentPhotos.length
                                       ? 'Deselect All'
                                       : 'Select All'}
                                 </Button>
                                 <Typography
                                    variant='body2'
                                    color='primary'
                                    sx={{ fontWeight: 'bold' }}
                                 >
                                    {selectedPhotos.size} photo
                                    {selectedPhotos.size !== 1 ? 's' : ''} selected
                                 </Typography>
                              </>
                           )}
                           <SharePhotosButton
                              entityType='event'
                              entityId={eventId!}
                              entityTitle={event?.title || 'Event'}
                              entityData={entityData}
                              selectedPhotos={Array.from(selectedPhotos)
                                 .map((id) => eventPhotos.find((p) => p.id === id))
                                 .filter(Boolean)}
                              variant='outlined'
                           />
                        </>
                     )}
                     <Button
                        variant='contained'
                        startIcon={<PhotoCameraIcon />}
                        onClick={() => setShowUploader((prev) => !prev)}
                     >
                        {showUploader ? 'Hide Uploader' : 'Add Photos'}
                     </Button>
                  </Box>
               </Box>
            )}

            {showUploader && event && (
               <Paper
                  sx={{
                     p: 3,
                     mb: 3,
                     bgcolor: INK2,
                     border: `1px solid ${BORDER}`,
                     borderRadius: 2,
                  }}
               >
                  <EventPhotoUploader
                     eventLocation={event.location || null}
                     eventId={event.id!}
                     onUploadComplete={handleUploadComplete}
                  />
               </Paper>
            )}

            {loadingPhotos ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
               </Box>
            ) : eventPhotos.length > 0 && !showUploader ? (
               <Box
                  sx={{
                     width: '100%',
                     overflow: 'hidden',
                     px: isMobile ? 0 : 0,
                  }}
               >
                  <Grid
                     container
                     spacing={isMobile ? 1 : 2}
                     sx={{
                        width: '100%',
                        margin: 0,
                        maxWidth: '100%',
                        '& .MuiGrid-item': {
                           paddingLeft: isMobile ? '4px !important' : undefined,
                           paddingTop: isMobile ? '4px !important' : undefined,
                           maxWidth: isMobile ? 'calc(50% - 4px)' : undefined,
                        },
                     }}
                  >
                     {eventPhotos.map((photo) => (
                        <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2} key={photo.id}>
                           <Card
                              sx={{
                                 position: 'relative',
                                 borderRadius: isMobile ? '12px' : 1,
                                 overflow: 'hidden',
                                 boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 2,
                                 border: selectedPhotos.has(photo.id)
                                    ? '3px solid #1976d2'
                                    : isMobile
                                      ? '1px solid #f0f0f0'
                                      : 'none',
                                 '&:hover': {
                                    boxShadow: isMobile ? '0 4px 12px rgba(0,0,0,0.15)' : 4,
                                    transform: isMobile ? 'none' : 'translateY(-2px)',
                                    transition: 'all 0.2s ease-in-out',
                                 },
                              }}
                           >
                              <Box sx={{ position: 'relative', width: '100%' }}>
                                 <CardMedia
                                    component='img'
                                    height={isMobile ? '140' : '140'}
                                    image={
                                       (photo as any).mediaType === 'video'
                                          ? ((photo as any).thumbnailUrl || photo.url)
                                          : photo.url
                                    }
                                    alt={photo.title}
                                    sx={{
                                       objectFit: 'cover',
                                       cursor: 'pointer',
                                       opacity: selectedPhotos.has(photo.id) ? 0.8 : 1,
                                    }}
                                    onClick={() =>
                                       window.open(
                                          (photo as any).mediaType === 'video'
                                             ? ((photo as any).videoUrl || photo.url)
                                             : photo.url,
                                          '_blank',
                                       )
                                    }
                                 />
                                 {(photo as any).mediaType === 'video' && (
                                    <VideoMediaBadge iconSize={isMobile ? 36 : 40} zIndex={1} />
                                 )}
                                 {hasUploaderMediaIds(
                                    (photo as any).userId,
                                    (photo as any).uploadedBy,
                                 ) &&
                                    (
                                       <MediaUploaderAttribution
                                          userId={(photo as any).userId || (photo as any).uploadedBy}
                                          onClick={() => {
                                             const uid =
                                                (photo as any).userId || (photo as any).uploadedBy;
                                             if (uid) setProfilePopoverUserId(uid);
                                          }}
                                          avatarSize={26}
                                          overlaySx={{ bottom: 4, left: 4, zIndex: 4 }}
                                       />
                                    )}
                              </Box>

                              {/* Selection checkbox - only visible for editors/owners */}
                              {canShare && (
                                 <Box
                                    position='absolute'
                                    top={8}
                                    left={8}
                                    bgcolor={
                                       selectedPhotos.has(photo.id) ? '#1976d2' : 'rgba(0,0,0,0.6)'
                                    }
                                    borderRadius='50%'
                                    p={0.3}
                                    sx={{
                                       transition: 'all 0.2s ease',
                                       border: selectedPhotos.has(photo.id)
                                          ? '2px solid white'
                                          : '2px solid transparent',
                                       zIndex: 10,
                                    }}
                                 >
                                    <Checkbox
                                       checked={selectedPhotos.has(photo.id)}
                                       onChange={(e) => {
                                          e.stopPropagation();
                                          togglePhotoSelection(photo.id);
                                       }}
                                       size='small'
                                       sx={{
                                          color: 'white',
                                          p: 0,
                                          '&.Mui-checked': {
                                             color: 'white',
                                          },
                                       }}
                                    />
                                 </Box>
                              )}

                              {(photo?.isTransformed || photo?.basePhotoId) && (
                                 <Typography
                                    sx={{
                                       position: 'absolute',
                                       bottom: 8,
                                       right: 8,
                                       backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                       padding: '5px 10px',
                                       borderRadius: '5px',
                                       zIndex: 5,
                                    }}
                                    variant='caption'
                                    color='secondary.main'
                                 >
                                    AI Generated
                                 </Typography>
                              )}

                              {!isMobile && (
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: 5,
                                       right: 5,
                                       display: 'flex',
                                       gap: 1,
                                       alignItems: 'center',
                                    }}
                                 >
                                    <Button
                                       sx={{
                                          backgroundColor: 'primary.main',
                                          color: 'white',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          padding: '5px 10px',
                                          minHeight: 'max-content',
                                          height: 'max-content',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          '&:hover': {
                                             backgroundColor: 'primary.dark',
                                          },
                                       }}
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          const photoIndex = eventPhotos.findIndex(
                                             (p) => p.id === photo.id,
                                          );
                                          setPhotoGalleryIndex(photoIndex);
                                          setPhotoGalleryOpen(true);
                                       }}
                                    >
                                       View
                                    </Button>
                                    {(canShare ||
                                       isEventOwner ||
                                       (currentUser &&
                                          (photo.uploadedBy === currentUser.uid ||
                                             photo.userId === currentUser.uid))) && (
                                       <Tooltip title='Delete photo'>
                                          <IconButton
                                             size='small'
                                             onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePhoto(photo);
                                             }}
                                             sx={{
                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                color: 'white',
                                                width: 32,
                                                height: 32,
                                                '&:hover': {
                                                   backgroundColor: 'error.main',
                                                   color: 'white',
                                                },
                                             }}
                                          >
                                             <DeleteIcon sx={{ fontSize: 18 }} />
                                          </IconButton>
                                       </Tooltip>
                                    )}
                                 </Box>
                              )}

                              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                                 {isMobile ? (
                                    // Mobile: Column layout to prevent overlapping
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                       <Typography
                                          variant='body2'
                                          sx={{
                                             fontSize: '0.875rem',
                                             fontWeight: 600,
                                             color: 'text.primary',
                                             overflow: 'hidden',
                                             textOverflow: 'ellipsis',
                                             whiteSpace: 'nowrap',
                                          }}
                                       >
                                          {photo?.title || 'Untitled'}
                                       </Typography>
                                       {photo.location && (
                                          <Typography
                                             variant='body2'
                                             color='text.secondary'
                                             sx={{
                                                fontSize: '0.75rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                             }}
                                          >
                                             {photo.location?.name}
                                          </Typography>
                                       )}
                                       {/* Mobile: View button at bottom */}
                                       <Button
                                          variant='outlined'
                                          size='small'
                                          sx={{
                                             mt: 1,
                                             fontSize: '0.75rem',
                                             py: 0.5,
                                             borderRadius: '20px',
                                             borderColor: '#00C4A7',
                                             color: '#00C4A7',
                                             '&:hover': {
                                                borderColor: '#00C4A7',
                                                backgroundColor: 'rgba(1, 148, 154, 0.04)',
                                             },
                                          }}
                                          onClick={() => {
                                             const photoIndex = eventPhotos.findIndex(
                                                (p) => p.id === photo.id,
                                             );
                                             setPhotoGalleryIndex(photoIndex);
                                             setPhotoGalleryOpen(true);
                                          }}
                                       >
                                          View Photo
                                       </Button>
                                       {/* Mobile: Like button */}
                                       {currentUser && (
                                          <Box sx={{ mt: 0.5 }}>
                                             <PhotoLikeButton
                                                photoId={photo.id}
                                                userId={currentUser.uid}
                                                initialLikeCount={photo.likeCount || 0}
                                                initialIsLiked={photo.isLiked || false}
                                                size='small'
                                                variant='compact'
                                                showCount={true}
                                                eventId={eventId}
                                             />
                                          </Box>
                                       )}
                                       {/* Mobile: Delete button - event owner or uploader */}
                                       {(canShare ||
                                          isEventOwner ||
                                          (currentUser &&
                                             (photo.uploadedBy === currentUser.uid ||
                                                photo.userId === currentUser.uid))) && (
                                          <Button
                                             variant='outlined'
                                             size='small'
                                             color='error'
                                             startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                             sx={{
                                                mt: 1,
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                borderRadius: '20px',
                                             }}
                                             onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePhoto(photo);
                                             }}
                                          >
                                             Delete
                                          </Button>
                                       )}
                                    </Box>
                                 ) : (
                                    // Desktop: Original layout
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-start',
                                       }}
                                    >
                                       {!photo?.isTransformed || !photo?.basePhotoId ? (
                                          <Box>
                                             <Typography variant='caption'>
                                                {photo?.title?.slice(0, 20)}
                                             </Typography>
                                             {photo.location && (
                                                <Typography
                                                   variant='body2'
                                                   color='text.secondary'
                                                   sx={{ mt: 1 }}
                                                >
                                                   {photo.location?.name}
                                                </Typography>
                                             )}
                                             {photo.description && (
                                                <Typography
                                                   variant='body2'
                                                   color='text.secondary'
                                                   sx={{ mt: 1 }}
                                                >
                                                   {photo?.description?.slice(0, 20)}
                                                </Typography>
                                             )}
                                          </Box>
                                       ) : (
                                          <Box>
                                             <Typography variant='caption'>
                                                {photo?.title?.slice(0, 5)}
                                             </Typography>
                                             {photo.location && (
                                                <Typography
                                                   variant='body2'
                                                   color='text.secondary'
                                                   sx={{ mt: 1 }}
                                                >
                                                   {photo.location?.name}
                                                </Typography>
                                             )}
                                             {photo.description && (
                                                <Typography
                                                   variant='body2'
                                                   color='text.secondary'
                                                   sx={{ mt: 1 }}
                                                >
                                                   {photo?.description?.slice(0, 20)}
                                                </Typography>
                                             )}
                                          </Box>
                                       )}

                                       <Box sx={{ display: 'flex', gap: 1 }}>
                                          {(!photo?.isTransformed || !photo?.basePhotoId) && (
                                             <Tooltip title='Transform Style'>
                                                <IconButton
                                                   color='primary'
                                                   sx={{ width: 24, height: 24 }}
                                                   onClick={() => {
                                                      setSelectedPhoto(photo);
                                                      setStyleModalOpen(true);
                                                   }}
                                                >
                                                   <StyleIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                             </Tooltip>
                                          )}
                                          {currentUser && (
                                             <PhotoLikeButton
                                                photoId={photo.id}
                                                userId={currentUser.uid}
                                                initialLikeCount={photo.likeCount || 0}
                                                initialIsLiked={photo.isLiked || false}
                                                size='small'
                                                variant='compact'
                                                showCount={true}
                                                eventId={eventId}
                                             />
                                          )}
                                          {currentUser && photo.userId === currentUser.uid && (
                                             <IconButton
                                                color='primary'
                                                onClick={() => handleCloseEditDialog(photo)}
                                                sx={{
                                                   width: 24,
                                                   height: 24,
                                                   '&:hover': {
                                                      backgroundColor: 'primary.light',
                                                      color: 'primary.contrastText',
                                                   },
                                                }}
                                             >
                                                <EditIcon sx={{ fontSize: 16 }} />
                                             </IconButton>
                                          )}
                                          {(canShare ||
                                             isEventOwner ||
                                             (currentUser &&
                                                (photo.uploadedBy === currentUser.uid ||
                                                   photo.userId === currentUser.uid))) && (
                                             <IconButton
                                                color='error'
                                                onClick={() => handleDeletePhoto(photo)}
                                                sx={{
                                                   width: 24,
                                                   height: 24,
                                                   '&:hover': {
                                                      backgroundColor: 'error.light',
                                                      color: 'error.contrastText',
                                                   },
                                                }}
                                             >
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                             </IconButton>
                                          )}
                                       </Box>
                                    </Box>
                                 )}
                              </CardContent>
                           </Card>
                        </Grid>
                     ))}
                  </Grid>
               </Box>
            ) : (
               <Box
                  sx={{
                     height: 300,
                     bgcolor: 'action.hover',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     borderRadius: 1,
                  }}
               >
                  <Box sx={{ textAlign: 'center' }}>
                     <PhotoCameraIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                     <Typography variant='body1' color='text.secondary' gutterBottom>
                        No photos yet
                     </Typography>
                     {(isEventOwner || isEventParticipant) && (
                        <Button
                           variant='contained'
                           startIcon={<PhotoCameraIcon />}
                           onClick={() => setShowUploader(true)}
                        >
                           Add Photos
                        </Button>
                     )}
                  </Box>
               </Box>
            )}
         </Box>
      );
   };

   const renderVirtualEvent = () => {
      if (!event || !event.isVirtual) {
         return (
            <Alert severity='info' sx={{ bgcolor: INK2, border: `1px solid ${BORDER}`, color: W60 }}>
               This event does not have a virtual component.
            </Alert>
         );
      }

      const now = new Date();
      const eventStartTime = event.startDate ? new Date(event.startDate) : null;
      const eventEndTime = event.endDate ? new Date(event.endDate) : null;

      const hasStarted = eventStartTime && now >= eventStartTime;
      const hasEnded = eventEndTime && now > eventEndTime;

      return (
         <Box sx={{ color: '#fff' }}>
            {/* Show recordings section for past events with recording enabled */}
            {hasEnded && event.recordingEnabled && (
               <Box sx={{ mb: 4 }}>
                  <RecordingsList eventId={event.id!} />
               </Box>
            )}

            {/* Show virtual event information */}
            <Card
               sx={{
                  mb: 3,
                  bgcolor: INK2,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
               }}
            >
               <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                     <VideocamIcon sx={{ color: TEAL, mr: 1, fontSize: 28 }} />
                     <Typography variant='h6' sx={{ color: '#fff' }}>
                        Virtual Event
                     </Typography>
                  </Box>

                  {/* Event Title and Description */}
                  {event.title && (
                     <Box sx={{ mb: 3 }}>
                        <Typography variant='h5' component='h2' gutterBottom sx={{ color: '#fff' }}>
                           {event.title}
                        </Typography>
                     </Box>
                  )}

                  {event.description && (
                     <Box sx={{ mb: 3 }}>
                        <Typography
                           variant='subtitle1'
                           gutterBottom
                           sx={{
                              fontFamily: 'DM Mono, monospace',
                              fontSize: 9,
                              letterSpacing: 2.5,
                              textTransform: 'uppercase',
                              color: TEAL,
                           }}
                        >
                           About this Event
                        </Typography>
                        <Box
                           sx={{
                              color: W60,
                              '& h1': {
                                 fontSize: '1.5rem',
                                 fontWeight: 'bold',
                                 marginBottom: '1rem',
                                 color: '#fff',
                              },
                              '& h2': {
                                 fontSize: '1.25rem',
                                 fontWeight: 'bold',
                                 marginBottom: '0.75rem',
                                 color: '#fff',
                              },
                              '& ul, & ol': {
                                 paddingLeft: '2rem',
                                 marginBottom: '1rem',
                              },
                              '& li': {
                                 marginBottom: '0.5rem',
                              },
                              '& p': {
                                 marginBottom: '1rem',
                                 lineHeight: '1.6',
                              },
                              '& a': {
                                 color: TEAL,
                                 textDecoration: 'none',
                                 '&:hover': {
                                    textDecoration: 'underline',
                                 },
                              },
                           }}
                           dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(event.description),
                           }}
                        />
                     </Box>
                  )}

                  {!hasStarted && (
                     <Alert
                        severity='info'
                        sx={{
                           mb: 3,
                           bgcolor: INK3,
                           border: `1px solid ${BORDER}`,
                           color: W60,
                        }}
                     >
                        <Typography variant='subtitle1' sx={{ color: '#fff' }}>
                           This virtual event has not started yet
                        </Typography>
                        <Typography variant='body2' sx={{ color: W60 }}>
                           The virtual meeting room will become available when the event starts on{' '}
                           {formatEventDate(event.startDate)}.
                        </Typography>
                     </Alert>
                  )}

                  {hasStarted && !hasEnded && (
                     <Alert
                        severity='success'
                        sx={{
                           mb: 3,
                           bgcolor: 'rgba(34, 197, 94, 0.1)',
                           border: '1px solid rgba(34, 197, 94, 0.25)',
                           color: W60,
                        }}
                     >
                        <Typography variant='subtitle1' sx={{ color: '#fff' }}>
                           Virtual event is now active
                        </Typography>
                        <Typography variant='body2' sx={{ color: W60 }}>
                           Use the "Join Meeting" button in the Details tab to join the video call.
                        </Typography>
                     </Alert>
                  )}

                  <Box sx={{ mt: 2 }}>
                     {event.recordingEnabled && (
                        <Typography variant='body2' sx={{ color: W35 }}>
                           Recording: Enabled
                        </Typography>
                     )}
                  </Box>
               </CardContent>
            </Card>
         </Box>
      );
   };

   const renderParticipants = () => {
      if (!event || (!event.members && !event.participants)) {
         return (
            <Box sx={{ textAlign: 'center', py: 4, color: '#fff' }}>
               <Typography variant='body1' sx={{ color: W35 }}>
                  No participants yet
               </Typography>
            </Box>
         );
      }

      // Use the new members array structure, fallback to legacy participants
      const members =
         event.members && event.members.length > 0 ? event.members : event.participants || [];

      // Check if participant sharing is enabled and user permissions
      const isOwnerOrEditor = userRole === 'owner' || userRole === 'editor';
      const canSeeAllParticipants = event.enableParticipantSharing || isOwnerOrEditor;

      // If participant sharing is disabled and user is not owner/editor, show limited view
      if (!canSeeAllParticipants) {
         return (
            <Box sx={{ textAlign: 'center', py: 4, color: '#fff' }}>
               <Typography variant='body1' sx={{ color: W35 }}>
                  Participant list is private for this event
               </Typography>
               <Typography variant='body2' sx={{ mt: 1, color: W35 }}>
                  Only event organizers can see the full participant list when Enable Participant
                  Collaboration is disabled
               </Typography>
               <Typography variant='caption' sx={{ mt: 1.5, display: 'block', color: W35 }}>
                  Event organizers can enable participant collaboration in event settings.
               </Typography>
            </Box>
         );
      }

      // Group members by role and status
      const confirmedParticipants = members.filter(
         (p: EventParticipant) => p.status === 'accepted' || !p.status
      ); // No status means accepted in new structure
      const pendingParticipants = members.filter((p: EventParticipant) => p.status === 'pending');

      // Check if a network request has been sent to this participant
      const hasNetworkRequestPending = (userId: string, email: string) => {
         return networkRequests.some(
            (r) => (r.toUserId === userId || r.toUserEmail === email) && r.status === 'pending'
         );
      };

      // Note: isInNetwork status is now pre-calculated in fetchEventDetails and stored in participant.isInNetwork

      const getRoleBorderColor = (role: string) => {
         if (role === 'owner') return 'primary.main';
         if (role === 'editor') return 'secondary.main';
         return 'grey.400';
      };

      return (
         <Box sx={{ color: '#fff' }}>
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: isMobile ? 2 : 3,
                  flexWrap: 'wrap',
                  gap: 2,
               }}
            >
               <Box>
                  <Typography
                     variant={isMobile ? 'h6' : 'h5'}
                     component='h2'
                     sx={{
                        fontFamily: 'Cormorant Garamond, serif',
                        fontSize: 22,
                        fontWeight: 300,
                        color: '#fff',
                     }}
                  >
                     Event Participants ({confirmedParticipants.length})
                  </Typography>
                  <Typography variant='body2' sx={{ mt: 0.5, color: W35 }}>
                     Connect and collaborate with everyone attending.
                  </Typography>
               </Box>

               {/* Send Reminder Button - only visible to event owner/editor */}
               {isEventOwner && confirmedParticipants.length > 0 && (
                  <Button
                     variant='contained'
                     size={isMobile ? 'small' : 'medium'}
                     startIcon={<SendIcon />}
                     onClick={handleOpenReminderModal}
                     sx={{
                        background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                        boxShadow: '0 4px 15px rgba(1, 148, 154, 0.3)',
                        '&:hover': {
                           background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                           boxShadow: '0 6px 20px rgba(1, 148, 154, 0.4)',
                        },
                     }}
                  >
                     Send Reminder
                  </Button>
               )}
            </Box>

            {confirmedParticipants.length === 0 ? (
               <Paper
                  variant='outlined'
                  sx={{
                     p: 3,
                     textAlign: 'center',
                     borderRadius: 2,
                     borderStyle: 'dashed',
                     borderColor: 'divider',
                  }}
               >
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                     Be the first to bring people together—invite participants and they&apos;ll show
                     up here once they join.
                  </Typography>
                  {isEventOwner && (
                     <Button
                        variant='outlined'
                        size='small'
                        onClick={() => setTabValue(4)}
                        sx={{ mt: 1 }}
                     >
                        Invite participants
                     </Button>
                  )}
               </Paper>
            ) : (
               <Stack spacing={2}>
                  {confirmedParticipants.map((participant, index) => (
                     <Paper
                        key={participant.userId || participant.email || index}
                        elevation={0}
                        sx={{
                           borderRadius: 2,
                           boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                           border: '1px solid',
                           borderColor: 'divider',
                           borderLeft: '4px solid',
                           borderLeftColor: getRoleBorderColor(participant.role || 'viewer'),
                           overflow: 'hidden',
                           transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                           '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 4,
                           },
                        }}
                     >
                        <ListItem
                           divider={false}
                           onClick={() => handleOpenParticipantProfile(participant)}
                           sx={{
                              cursor: 'pointer',
                              py: isMobile ? 2 : 1.5,
                              px: isMobile ? 2 : 2,
                              '&:hover': {
                                 bgcolor: 'action.hover',
                              },
                           }}
                           secondaryAction={
                              !isMobile ? (
                                 <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {participant.userId === currentUser?.uid ? (
                                       <Chip
                                          label='You'
                                          size='small'
                                          color='primary'
                                          variant='outlined'
                                          sx={{ fontWeight: 'medium' }}
                                       />
                                    ) : (
                                       <>
                                          {/* Start Conversation Button - visible to all users */}
                                          {participant.isInNetwork ||
                                          addedContacts.has(
                                             participant.userId || participant.email,
                                          ) ? (
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={<CheckIcon />}
                                                disabled
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   color: 'success.main',
                                                   borderColor: 'success.main',
                                                }}
                                             >
                                                Added
                                             </Button>
                                          ) : (
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={
                                                   addingContact ===
                                                   (participant.userId || participant.email) ? (
                                                      <CircularProgress size={12} />
                                                   ) : (
                                                      <ContactsIcon />
                                                   )
                                                }
                                                onClick={() => addToContacts(participant)}
                                                disabled={
                                                   addingContact ===
                                                   (participant.userId || participant.email)
                                                }
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   borderRadius: '4px',
                                                   textTransform: 'none',
                                                   fontWeight: 400,
                                                   bgcolor: 'transparent',
                                                   color: 'primary.main',
                                                   borderColor: 'primary.main',
                                                   '&:hover': {
                                                      bgcolor: 'primary.main',
                                                      color: 'white',
                                                      borderColor: 'primary.main',
                                                   },
                                                }}
                                             >
                                                Start the conversation
                                             </Button>
                                          )}

                                          {/* Management Buttons - only for editors/owners */}
                                          {isEventOwner && (
                                             <>
                                                <Button
                                                   variant='outlined'
                                                   size='small'
                                                   startIcon={<EditIcon />}
                                                   onClick={() =>
                                                      handleEditParticipant(participant)
                                                   }
                                                   sx={{
                                                      fontSize: '0.75rem',
                                                      py: 0.5,
                                                      px: 1.5,
                                                      ml: 1,
                                                      borderColor: 'secondary.main',
                                                      color: 'secondary.main',
                                                      '&:hover': {
                                                         bgcolor: 'secondary.main',
                                                         color: 'white',
                                                         borderColor: 'secondary.main',
                                                      },
                                                   }}
                                                >
                                                   Edit
                                                </Button>
                                                <Button
                                                   variant='outlined'
                                                   size='small'
                                                   startIcon={<DeleteIcon />}
                                                   onClick={() =>
                                                      handleDeleteParticipant(participant)
                                                   }
                                                   sx={{
                                                      fontSize: '0.75rem',
                                                      py: 0.5,
                                                      px: 1.5,
                                                      ml: 1,
                                                      borderColor: 'error.main',
                                                      color: 'error.main',
                                                      '&:hover': {
                                                         bgcolor: 'error.main',
                                                         color: 'white',
                                                         borderColor: 'error.main',
                                                      },
                                                   }}
                                                >
                                                   Delete
                                                </Button>
                                             </>
                                          )}
                                       </>
                                    )}
                                    {hasNetworkRequestPending(
                                       participant.userId || '',
                                       participant.email,
                                    ) && (
                                       <Tooltip title="You've sent a network request to this person">
                                          <Chip
                                             label='Request Sent'
                                             size='small'
                                             color='info'
                                             variant='outlined'
                                             sx={{ fontWeight: 'medium' }}
                                          />
                                       </Tooltip>
                                    )}
                                 </Box>
                              ) : null
                           }
                        >
                           <ListItemAvatar>
                              <Avatar
                                 src={
                                    participant.profileData?.profilePicture ||
                                    participant.profileData?.photoURL ||
                                    participant.profileData?.profilePhoto ||
                                    participant.profileData?.avatar ||
                                    participant.photoURL ||
                                    undefined
                                 }
                                 alt={participant.displayName || participant.email}
                              >
                                 {(
                                    participant.profileData?.displayName ||
                                    participant.profileData?.name ||
                                    participant.profileData?.fullName ||
                                    participant.displayName ||
                                    participant.email ||
                                    '?'
                                 )
                                    .charAt(0)
                                    .toUpperCase()}
                              </Avatar>
                           </ListItemAvatar>
                           <ListItemText
                              primary={
                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                       variant='body1'
                                       sx={{
                                          fontWeight: isMobile ? 500 : 400,
                                          fontSize: isMobile ? '1rem' : '0.875rem',
                                       }}
                                    >
                                       {participant.displayName || participant.email.split('@')[0]}
                                    </Typography>
                                    {participant.role === 'owner' && (
                                       <Chip
                                          size='small'
                                          label='Owner'
                                          sx={{
                                             bgcolor: isMobile ? '#00C4A7' : 'primary.main',
                                             color: 'white',
                                             fontSize: '0.75rem',
                                             height: '24px',
                                             borderRadius: '12px',
                                             fontWeight: 500,
                                          }}
                                       />
                                    )}
                                    {participant.role === 'editor' && (
                                       <Chip
                                          size='small'
                                          label='Editor'
                                          sx={{
                                             bgcolor: isMobile ? '#004369' : 'secondary.main',
                                             color: 'white',
                                             fontSize: '0.75rem',
                                             height: '24px',
                                             borderRadius: '12px',
                                             fontWeight: 500,
                                          }}
                                       />
                                    )}
                                    {participant.role === 'viewer' && (
                                       <Chip
                                          size='small'
                                          label='Viewer'
                                          sx={{
                                             bgcolor: isMobile ? '#9e9e9e' : 'info.main',
                                             color: 'white',
                                             fontSize: '0.75rem',
                                             height: '24px',
                                             borderRadius: '12px',
                                             fontWeight: 500,
                                          }}
                                       />
                                    )}
                                 </Box>
                              }
                              secondary={
                                 <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{
                                       fontSize: isMobile ? '0.875rem' : '0.75rem',
                                       mt: isMobile ? 0.5 : 0,
                                    }}
                                 >
                                    {participant.email}
                                    {participant.joinedAt && (
                                       <Typography
                                          component='span'
                                          sx={{ ml: 1, fontSize: '0.75rem' }}
                                       >
                                          • Joined{' '}
                                          {new Date(
                                             (participant.joinedAt as any)?.seconds
                                                ? (participant.joinedAt as any).seconds * 1000
                                                : participant.joinedAt,
                                          ).toLocaleDateString()}
                                       </Typography>
                                    )}
                                 </Typography>
                              }
                           />

                           {/* Mobile: Show buttons below content in row layout */}
                           {isMobile && (
                              <Box
                                 sx={{
                                    mt: 1,
                                    ml: 0,
                                    mr: 0,
                                    px: 2,
                                    py: 1,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 1,
                                    width: '100%',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                    borderRadius: 1,
                                    position: 'relative',
                                    zIndex: 1,
                                 }}
                              >
                                 {participant.userId === currentUser?.uid ? (
                                    <Chip
                                       label='You'
                                       size='small'
                                       color='primary'
                                       variant='outlined'
                                       sx={{ fontWeight: 'medium' }}
                                    />
                                 ) : (
                                    <>
                                       {participant.isInNetwork ||
                                       addedContacts.has(
                                          participant.userId || participant.email,
                                       ) ? (
                                          <Button
                                             variant='outlined'
                                             size='small'
                                             startIcon={<CheckIcon />}
                                             disabled
                                             sx={{
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1.5,
                                                color: 'success.main',
                                                borderColor: 'success.main',
                                                borderRadius: '20px',
                                                width: 'auto',
                                             }}
                                          >
                                             Added
                                          </Button>
                                       ) : (
                                          <Button
                                             variant='contained'
                                             size='small'
                                             startIcon={
                                                addingContact ===
                                                (participant.userId || participant.email) ? (
                                                   <CircularProgress size={12} />
                                                ) : (
                                                   <ContactsIcon />
                                                )
                                             }
                                             onClick={() => addToContacts(participant)}
                                             disabled={
                                                addingContact ===
                                                (participant.userId || participant.email)
                                             }
                                             sx={{
                                                fontSize: '0.75rem',
                                                py: 0.75,
                                                px: 2,
                                                borderRadius: '20px',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                width: 'auto',
                                                '&:hover': {
                                                   bgcolor: 'primary.dark',
                                                },
                                             }}
                                          >
                                             Start the conversation
                                          </Button>
                                       )}

                                       {/* Management Buttons - only for editors/owners - in COLUMN */}
                                       {isEventOwner && (
                                          <>
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={<EditIcon />}
                                                onClick={() => handleEditParticipant(participant)}
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   borderRadius: '20px',
                                                   borderColor: 'secondary.main',
                                                   color: 'secondary.main',
                                                   width: 'auto',
                                                   '&:hover': {
                                                      bgcolor: 'secondary.main',
                                                      color: 'white',
                                                      borderColor: 'secondary.main',
                                                   },
                                                }}
                                             >
                                                Edit
                                             </Button>
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={<DeleteIcon />}
                                                onClick={() => handleDeleteParticipant(participant)}
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   borderRadius: '20px',
                                                   borderColor: 'error.main',
                                                   color: 'error.main',
                                                   width: 'auto',
                                                   '&:hover': {
                                                      bgcolor: 'error.main',
                                                      color: 'white',
                                                      borderColor: 'error.main',
                                                   },
                                                }}
                                             >
                                                Delete
                                             </Button>
                                          </>
                                       )}
                                    </>
                                 )}
                              </Box>
                           )}
                        </ListItem>
                     </Paper>
                  ))}
               </Stack>
            )}

            {/* Show pending participants section for event owners */}
            {isEventOwner && pendingParticipants.length > 0 && (
               <Box sx={{ mt: 4 }}>
                  <Typography variant='h6' gutterBottom>
                     Pending Invitations ({pendingParticipants.length})
                  </Typography>
                  <List>
                     {pendingParticipants.map((participant, index) => (
                        <ListItem
                           key={participant.userId || participant.email || index}
                           divider={index < pendingParticipants.length - 1}
                           secondaryAction={
                              <Button
                                 variant='outlined'
                                 color='error'
                                 size='small'
                                 onClick={() => {
                                    /* handle canceling invitation */
                                 }}
                              >
                                 Cancel
                              </Button>
                           }
                        >
                           <ListItemAvatar>
                              <Avatar
                                 src={
                                    participant.profileData?.profilePicture ||
                                    participant.profileData?.photoURL ||
                                    participant.profileData?.profilePhoto ||
                                    participant.profileData?.avatar ||
                                    participant.photoURL ||
                                    undefined
                                 }
                                 alt={participant.displayName || participant.email}
                              >
                                 {(
                                    participant.profileData?.displayName ||
                                    participant.profileData?.name ||
                                    participant.profileData?.fullName ||
                                    participant.displayName ||
                                    participant.email ||
                                    '?'
                                 )
                                    .charAt(0)
                                    .toUpperCase()}
                              </Avatar>
                           </ListItemAvatar>
                           <ListItemText
                              primary={participant.displayName || participant.email.split('@')[0]}
                              secondary={
                                 <>
                                    {participant.email}
                                    <Typography variant='caption' component='span' sx={{ ml: 1 }}>
                                       (Invited{' '}
                                       {participant.invitedAt
                                          ? new Date(participant.invitedAt).toLocaleDateString()
                                          : 'recently'}
                                       )
                                    </Typography>
                                 </>
                              }
                           />
                        </ListItem>
                     ))}
                  </List>
               </Box>
            )}
         </Box>
      );
   };

   const renderAlbums = () => {
      return (
         <Box sx={{ color: '#fff' }}>
         <LinkedAlbums
            eventId={eventId}
            userId={currentUser?.uid || ''}
            userEmail={currentUser?.email || ''}
            photos={eventPhotos}
            title='Event Albums'
            refreshTrigger={albumsRefreshTrigger}
         />
         </Box>
      );
   };

   const renderEventSharing = () => {
      if (!event) return null;

      return (
         <Box sx={{ p: 0, color: '#fff' }}>
            {/* Public Link Section with Download Button */}
            <Box sx={{ maxWidth: '600px' }}>
               <Card
                  sx={{
                     bgcolor: INK2,
                     border: `1px solid ${BORDER}`,
                     borderRadius: 2,
                  }}
               >
                  <CardContent>
                     <Typography
                        variant='h6'
                        sx={{
                           mb: 2,
                           display: 'flex',
                           alignItems: 'center',
                           fontFamily: 'DM Mono, monospace',
                           fontSize: 9,
                           letterSpacing: 2.5,
                           textTransform: 'uppercase',
                           color: TEAL,
                        }}
                     >
                        <LinkIcon sx={{ mr: 1 }} />
                        Public Event Link
                     </Typography>
                     <Typography variant='body2' sx={{ mb: 2, color: W35 }}>
                        Share this link to allow anyone to view and request to join your event.
                     </Typography>
                     <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                           value={`${window.location.origin}/events/public/${eventId}?source=shareable_link`}
                           fullWidth
                           InputProps={{
                              readOnly: true,
                              sx: {
                                 color: W60,
                                 '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                              },
                           }}
                           size='small'
                           sx={{
                              '& .MuiInputBase-input': { color: W60 },
                              '& .MuiOutlinedInput-root': { bgcolor: INK3 },
                           }}
                        />
                        <Button
                           variant='outlined'
                           onClick={() => {
                              navigator.clipboard.writeText(
                                 `${window.location.origin}/events/public/${eventId}?source=shareable_link`
                              );
                              // TODO: Show success toast
                           }}
                           sx={{
                              borderColor: BORDER2,
                              color: W60,
                              '&:hover': { borderColor: TEAL, color: TEAL },
                           }}
                        >
                           Copy
                        </Button>
                     </Box>

                     {/* Download Flyer Button - opens MUI flyer dialog */}
                     <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                           variant='contained'
                           startIcon={<DownloadIcon />}
                           onClick={() => setFlyerDialogOpen(true)}
                           sx={{
                              maxWidth: '200px',
                              height: '40px',
                              minWidth: '160px',
                              bgcolor: TEAL,
                              color: '#000',
                              fontWeight: 700,
                              '&:hover': { bgcolor: 'rgba(0,196,167,0.9)' },
                           }}
                        >
                           Download Flyer
                        </Button>
                     </Box>
                  </CardContent>
               </Card>
            </Box>
         </Box>
      );
   };

   const fetchWaitlist = async () => {
      console.log('fetchWaitlist called:', { eventId, isEventOwner });
      if (!eventId || !isEventOwner) {
         console.log('fetchWaitlist: conditions not met, returning');
         return;
      }

      try {
         setLoadingWaitlist(true);
         console.log('Fetching waitlist for event:', eventId);

         // Get auth headers
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/events/${eventId}/waitlist`,
            {
               method: 'GET',
               headers,
            }
         );
         const result = await response.json();
         console.log('Waitlist response:', result);

         if (result.success) {
            console.log('Frontend received waitlist data:', {
               success: result.success,
               waitingListLength: result.waitingList?.length || 0,
               waitingList: result.waitingList,
            });
            result.waitingList?.forEach((entry: any, index: number) => {
               console.log(`Entry ${index}:`, {
                  id: entry.id,
                  status: entry.status,
                  name: entry.name,
                  email: entry.email,
                  requestedAt: entry.requestedAt,
                  requestedAtType: typeof entry.requestedAt,
               });
            });
            setWaitingList(result.waitingList || []);
            console.log('Waitlist state updated with:', result.waitingList?.length || 0, 'entries');
         } else {
            console.log('Waitlist API returned error:', result);
         }
      } catch (err) {
         console.error('Error fetching waitlist:', err);
      } finally {
         setLoadingWaitlist(false);
      }
   };

   const renderWaitlist = () => {
      const handleApproveUser = async (waitingListId: string, userId: string) => {
         try {
            // Add user to approving set
            setApprovingUsers((prev) => new Set(prev).add(waitingListId));

            // Get auth headers
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/${eventId}/waitlist/${waitingListId}/approve`,
               {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({ userId }),
               }
            );

            const result = await response.json();

            if (result.success) {
               setSnackbarMessage('User approved successfully');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               fetchWaitlist(); // Refresh waitlist
               fetchEventDetails(); // Refresh event data to show new participant
            } else {
               setSnackbarMessage(result.error || 'Failed to approve user');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (err) {
            console.error('Error approving user:', err);
            setSnackbarMessage('Failed to approve user');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            // Remove user from approving set
            setApprovingUsers((prev) => {
               const newSet = new Set(prev);
               newSet.delete(waitingListId);
               return newSet;
            });
         }
      };

      const handleRejectUser = async (waitingListId: string) => {
         try {
            // Add user to rejecting set
            setRejectingUsers((prev) => new Set(prev).add(waitingListId));

            // Get auth headers
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/${eventId}/waitlist/${waitingListId}/reject`,
               {
                  method: 'POST',
                  headers,
               }
            );

            const result = await response.json();

            if (result.success) {
               setSnackbarMessage('User request rejected');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               fetchWaitlist(); // Refresh waitlist
            } else {
               setSnackbarMessage(result.error || 'Failed to reject user');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (err) {
            console.error('Error rejecting user:', err);
            setSnackbarMessage('Failed to reject user');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            // Remove user from rejecting set
            setRejectingUsers((prev) => {
               const newSet = new Set(prev);
               newSet.delete(waitingListId);
               return newSet;
            });
         }
      };

      return (
         <Box sx={{ p: 0, color: '#fff' }}>
            <Typography
               variant='h5'
               sx={{
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 22,
                  fontWeight: 300,
                  color: '#fff',
               }}
            >
               <PeopleIcon sx={{ mr: 1 }} />
               Event Waitlist
            </Typography>

            {loadingWaitlist ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: TEAL }} />
               </Box>
            ) : waitingList.length === 0 ? (
               <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant='body1' sx={{ color: W35 }}>
                     No requests yet
                  </Typography>
               </Box>
            ) : (
               <Box>
                  <Typography variant='body2' sx={{ mb: 2, color: W35 }}>
                     {waitingList.length} request{waitingList.length !== 1 ? 's' : ''} total
                  </Typography>

                  <List>
                     {waitingList.map((request) => {
                        const getStatusColor = (status: string) => {
                           switch (status) {
                              case 'approved':
                                 return 'success';
                              case 'rejected':
                                 return 'error';
                              case 'pending':
                                 return 'warning';
                              default:
                                 return 'default';
                           }
                        };

                        const getStatusText = (status: string) => {
                           switch (status) {
                              case 'approved':
                                 return 'Approved';
                              case 'rejected':
                                 return 'Rejected';
                              case 'pending':
                                 return 'Pending';
                              default:
                                 return 'Unknown';
                           }
                        };

                        return (
                           <ListItem
                              key={request.id}
                              sx={{
                                 border: `1px solid ${BORDER}`,
                                 borderRadius: 1,
                                 mb: 1,
                                 bgcolor: INK2,
                              }}
                           >
                              <ListItemAvatar>
                                 <Avatar>
                                    <PersonIcon />
                                 </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                 primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                       <Typography variant='subtitle1'>{request.name}</Typography>
                                       <Chip
                                          label={getStatusText(request.status)}
                                          color={getStatusColor(request.status)}
                                          size='small'
                                       />
                                    </Box>
                                 }
                                 secondary={
                                    <Box>
                                       <Typography variant='body2' color='text.secondary'>
                                          {request.email}
                                       </Typography>
                                       <Typography variant='caption' color='text.secondary'>
                                          Requested:{' '}
                                          {request.requestedAt
                                             ? (() => {
                                                  // Backend now sends converted timestamps as ISO strings
                                                  const date = new Date(request.requestedAt);
                                                  return date.toLocaleDateString('en-US', {
                                                     year: 'numeric',
                                                     month: 'short',
                                                     day: 'numeric',
                                                     hour: '2-digit',
                                                     minute: '2-digit',
                                                  });
                                               })()
                                             : 'Unknown'}
                                       </Typography>
                                       {request.approvedAt && (
                                          <Typography
                                             variant='caption'
                                             color='text.secondary'
                                             sx={{ display: 'block' }}
                                          >
                                             Approved:{' '}
                                             {new Date(request.approvedAt).toLocaleDateString(
                                                'en-US',
                                                {
                                                   year: 'numeric',
                                                   month: 'short',
                                                   day: 'numeric',
                                                   hour: '2-digit',
                                                   minute: '2-digit',
                                                }
                                             )}
                                          </Typography>
                                       )}
                                       {request.rejectedAt && (
                                          <Typography
                                             variant='caption'
                                             color='text.secondary'
                                             sx={{ display: 'block' }}
                                          >
                                             Rejected:{' '}
                                             {new Date(request.rejectedAt).toLocaleDateString(
                                                'en-US',
                                                {
                                                   year: 'numeric',
                                                   month: 'short',
                                                   day: 'numeric',
                                                   hour: '2-digit',
                                                   minute: '2-digit',
                                                }
                                             )}
                                          </Typography>
                                       )}
                                    </Box>
                                 }
                              />
                              <ListItemSecondaryAction>
                                 {request.status === 'pending' ? (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                       <Button
                                          size='small'
                                          variant='contained'
                                          color='success'
                                          disabled={
                                             approvingUsers.has(request.id) ||
                                             rejectingUsers.has(request.id)
                                          }
                                          onClick={() =>
                                             handleApproveUser(request.id, request.userId)
                                          }
                                          startIcon={
                                             approvingUsers.has(request.id) ? (
                                                <CircularProgress size={16} color='inherit' />
                                             ) : null
                                          }
                                       >
                                          {approvingUsers.has(request.id)
                                             ? 'Approving...'
                                             : 'Approve'}
                                       </Button>
                                       <Button
                                          size='small'
                                          variant='outlined'
                                          color='error'
                                          disabled={
                                             approvingUsers.has(request.id) ||
                                             rejectingUsers.has(request.id)
                                          }
                                          onClick={() => handleRejectUser(request.id)}
                                          startIcon={
                                             rejectingUsers.has(request.id) ? (
                                                <CircularProgress size={16} color='inherit' />
                                             ) : null
                                          }
                                       >
                                          {rejectingUsers.has(request.id)
                                             ? 'Rejecting...'
                                             : 'Reject'}
                                       </Button>
                                    </Box>
                                 ) : (
                                    <Typography variant='caption' sx={{ color: W35 }}>
                                       {getStatusText(request.status)}
                                    </Typography>
                                 )}
                              </ListItemSecondaryAction>
                           </ListItem>
                        );
                     })}
                  </List>
               </Box>
            )}
         </Box>
      );
   };

   const renderAnalytics = () => {
      if (!eventId) return null;

      return (
         <Box sx={{ p: 0, color: '#fff' }}>
            <EventAnalytics eventId={eventId} />
         </Box>
      );
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
         </Box>
      );
   }

   if (error) {
      return (
         <Container maxWidth='md'>
            <Alert severity='error' sx={{ mt: 4 }}>
               {error}
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
               <Button
                  variant='contained'
                  onClick={() => navigate('/events')}
                  startIcon={<ArrowBackIcon />}
               >
                  Back to Events
               </Button>
            </Box>
         </Container>
      );
   }

   if (!event) {
      return (
         <Container maxWidth='md'>
            <Alert severity='warning' sx={{ mt: 4 }}>
               Event not found
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
               <Button
                  variant='contained'
                  onClick={() => navigate('/events')}
                  startIcon={<ArrowBackIcon />}
               >
                  Back to Events
               </Button>
            </Box>
         </Container>
      );
   }

   return (
      <Container
         maxWidth={isMobile ? false : 'lg'}
         sx={{
            px: isMobile ? 0 : 3,
            width: '100%',
            overflow: 'hidden',
            bgcolor: INK,
            minHeight: '100vh',
         }}
      >
         {/* Top bar: Back to Events + optional avatar */}
            <Box
               sx={{
               height: 56,
               bgcolor: 'rgba(8, 10, 15, 0.92)',
               backdropFilter: 'blur(16px)',
               borderBottom: `1px solid ${BORDER}`,
                  display: 'flex',
               alignItems: 'center',
               px: { xs: 2, md: 3.5 },
               gap: 2,
               flexShrink: 0,
            }}
         >
            <Box
               component='button'
                     onClick={() => navigate('/events')}
                     sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontFamily: 'DM Mono, monospace',
                  fontSize: 10,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  color: W35,
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  '&:hover': { color: TEAL },
               }}
            >
               <ArrowBackIcon sx={{ fontSize: 16 }} />
                     Back to Events
            </Box>
               </Box>

         {/* Event hero: compact on both mobile and desktop */}
               <Box
                  sx={{
               bgcolor: INK2,
               borderBottom: `1px solid ${BORDER}`,
               p: { xs: 2, md: 3 },
                     display: 'flex',
               gap: { xs: 1.5, md: 2.5 },
               alignItems: { xs: 'stretch', md: 'flex-end' },
               flexDirection: { xs: 'column', md: 'row' },
               flexShrink: 0,
            }}
         >
            {/* Hero cover 80x80 */}
                     <Box
                        sx={{
                  width: 80,
                  height: 80,
                     borderRadius: 2,
                  bgcolor: INK3,
                  border: `1px solid ${BORDER2}`,
                  flexShrink: 0,
                     overflow: 'hidden',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                  }}
               >
               {event.coverImageUrl ? (
                  <Box
                     component='img'
                     src={event.coverImageUrl}
                     alt=''
                     sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
               ) : (
                  <EventIcon sx={{ fontSize: 28, color: W35 }} />
               )}
            </Box>

            {/* Hero info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                           <Chip
                  label={eventTypeNames[event.eventType as EventType] || event.eventType}
                  size='small'
                              sx={{
                     fontFamily: 'DM Mono, monospace',
                     fontSize: 9,
                     letterSpacing: 2,
                     textTransform: 'uppercase',
                     color: TEAL,
                     bgcolor: 'rgba(0, 196, 167, 0.1)',
                     border: '1px solid rgba(0, 196, 167, 0.22)',
                                 mb: 1,
                              }}
                           />
                           <Typography
                  variant='h1'
                              sx={{
                     fontFamily: 'Cormorant Garamond, serif',
                     fontSize: { xs: '1.5rem', md: '1.75rem' },
                     fontWeight: 300,
                     color: '#fff',
                     mb: 0.75,
                     lineHeight: 1.1,
                              }}
                           >
                              {event.title}
                           </Typography>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: W35 }}>
                     {event.isPublic ? (
                        <>
                           <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#22c55e' }} />
                           Public Event
                        </>
                     ) : (
                        <>
                           <LockIcon sx={{ fontSize: 14 }} />
                           Private Event
                        </>
                     )}
                  </Box>
                  <Typography component='span' sx={{ color: W35, fontSize: 12 }}>
                     •
                  </Typography>
                  <Typography component='span' sx={{ color: W35, fontSize: 12 }}>
                                 Hosted by{' '}
                     {(event.members || []).find((p) => p.role === 'owner')?.displayName || 'Unknown'}
                              </Typography>
                  <Typography component='span' sx={{ color: W35, fontSize: 12 }}>
                     •
                  </Typography>
                  <Typography component='span' sx={{ color: W35, fontSize: 12 }}>
                     {formatEventDate(event.startDate)}
                  </Typography>
                  {event.location && (
                     <>
                        <Typography component='span' sx={{ color: W35, fontSize: 12 }}>•</Typography>
                        <Typography component='span' sx={{ color: W35, fontSize: 12 }}>
                           {getLocationDisplayName(event.location)}
                        </Typography>
                     </>
                  )}
                           </Box>
                        </Box>

            {/* Hero actions */}
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexDirection: { xs: 'column', md: 'row' } }}>
               {event?.isVirtual && (
                  isEventOwner ? (
                              <Button
                                 startIcon={<VideocamIcon />}
                                 onClick={handleJoinMeeting}
                                 size='medium'
                                 sx={{
                           bgcolor: 'rgba(255,255,255,0.05)',
                           border: `1px solid ${BORDER2}`,
                           color: W60,
                           '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: BORDER2 },
                                 }}
                              >
                                 Start Virtual Event
                              </Button>
                           ) : (
                              <Button
                                 startIcon={<VideocamIcon />}
                                 onClick={handleJoinMeeting}
                                 size='medium'
                                 sx={{
                           bgcolor: TEAL,
                           color: '#000',
                           fontWeight: 700,
                           '&:hover': { bgcolor: 'rgba(0,196,167,0.9)' },
                                 }}
                              >
                                 Join Event
                              </Button>
                  )
                           )}
                           {isEventOwner && (
                              <>
                                 <Button
                                    startIcon={<EditIcon />}
                                    onClick={handleEdit}
                                    size='medium'
                                    sx={{
                                       bgcolor: TEAL,
                                       color: '#000',
                                       fontWeight: 700,
                                       '&:hover': { bgcolor: 'rgba(0,196,167,0.9)' },
                                    }}
                                 >
                                    Edit Event
                                 </Button>
                                 <Button
                                    startIcon={
                                       exporting ? (
                                          <CircularProgress size={16} sx={{ color: '#000' }} />
                                       ) : (
                                          <DownloadIcon />
                                       )
                                    }
                                    onClick={handleExport}
                                    disabled={exporting}
                                    size='medium'
                                    sx={{
                                       bgcolor: TEAL,
                                       color: '#000',
                                       fontWeight: 700,
                                       '&:hover': { bgcolor: 'rgba(0,196,167,0.9)' },
                                       '&.Mui-disabled': { bgcolor: 'rgba(0,196,167,0.5)', color: '#000' },
                                    }}
                                 >
                                    {exporting ? 'Exporting…' : 'Export'}
                                 </Button>
                              </>
                           )}
                        </Box>
                     </Box>

         {/* Tabs bar */}
         <Box
            sx={{
               borderBottom: `1px solid ${BORDER}`,
               px: { xs: 2, md: 3.5 },
               bgcolor: INK2,
               flexShrink: 0,
               overflowX: 'auto',
            }}
         >
            <Tabs
               value={tabValue}
               onChange={handleTabChange}
               variant={isMobile ? 'scrollable' : 'standard'}
               scrollButtons={isMobile ? 'auto' : false}
               allowScrollButtonsMobile
               sx={{
                  minHeight: 48,
                  '& .MuiTabs-indicator': { backgroundColor: TEAL, height: 2 },
                  '& .MuiTab-root': {
                     minWidth: 'auto',
                     fontSize: 13,
                     fontWeight: 500,
                     color: W35,
                     textTransform: 'none',
                     py: 1.625,
                     px: 2.25,
                  },
                  '& .Mui-selected': { color: TEAL, fontWeight: 600 },
                  '& .MuiTabs-flexContainer': { gap: 0 },
               }}
            >
               <Tab label='Details' />
               <Tab label='Photos' />
               <Tab label='Albums' />
               <Tab label='Participants' />
               {event?.isVirtual && <Tab label='Virtual Event' />}
               {(isEventOwner || userRole === 'owner' || userRole === 'editor') && (
                  <Tab label='Event Sharing' />
               )}
               {(isEventOwner || userRole === 'owner' || userRole === 'editor') && (
                  <Tab label='Waitlist' />
               )}
               {(isEventOwner || userRole === 'owner' || userRole === 'editor') && (
                  <Tab label='Analytics' />
               )}
            </Tabs>
         </Box>

         {/* Tab content - scrollable area */}
         <Box
            sx={{
               flex: 1,
               overflowY: 'auto',
               py: 3.5,
               px: { xs: 2, md: 3.5 },
               mb: 4,
            }}
         >
            {(() => {
               // Calculate tab indices based on available tabs
               let currentTabIndex = 0;

               // Tab 0: Details
               if (tabValue === currentTabIndex++) return renderEventInfo();

               // Tab 1: Photos
               if (tabValue === currentTabIndex++) return renderPhotos();

               // Tab 2: Albums
               if (tabValue === currentTabIndex++) return renderAlbums();

               // Tab 3: Participants
               if (tabValue === currentTabIndex++) return renderParticipants();

               // Tab 4: Virtual Event (if available)
               if (event?.isVirtual && tabValue === currentTabIndex++) return renderVirtualEvent();

               // Tab 5: Event Sharing (if user has permissions)
               if (
                  (isEventOwner || userRole === 'owner' || userRole === 'editor') &&
                  tabValue === currentTabIndex++
               )
                  return renderEventSharing();

               // Tab 6: Waitlist (if user has permissions)
               if (
                  (isEventOwner || userRole === 'owner' || userRole === 'editor') &&
                  tabValue === currentTabIndex++
               )
                  return renderWaitlist();

               // Tab 7: Analytics (if user has permissions)
               if (
                  (isEventOwner || userRole === 'owner' || userRole === 'editor') &&
                  tabValue === currentTabIndex++
               )
                  return renderAnalytics();

               // Fallback to Details if tab value is invalid
               return renderEventInfo();
            })()}
         </Box>

         <EditPhotoDialog
            open={showEditDialog}
            photo={selectedPhoto}
            onClose={() => setShowEditDialog(false)}
         />

         <StyleTransformModal
            open={styleModalOpen}
            onClose={() => setStyleModalOpen(false)}
            photoUrl={selectedPhoto?.url || ''}
            photoId={selectedPhoto?.id || ''}
            onSave={handleSave}
         />
         {/* Delete confirmation dialog */}
         <Dialog
            open={openDeleteDialog}
            onClose={handleDeleteCancel}
            aria-labelledby='delete-dialog-title'
            aria-describedby='delete-dialog-description'
            PaperProps={{
               sx: {
                  bgcolor: INK2,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
               },
            }}
         >
            <DialogTitle id='delete-dialog-title' sx={{ color: '#fff' }}>
               Delete Photo?
            </DialogTitle>
            <DialogContent>
               <Typography id='delete-dialog-description' sx={{ color: W60 }}>
                  Are you sure you want to delete this photo? This action cannot be undone.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleDeleteCancel} sx={{ color: W60 }}>
                  Cancel
               </Button>
               <Button onClick={handleDeleteConfirm} color='error' autoFocus>
                  Delete Photo
               </Button>
            </DialogActions>
         </Dialog>

         <Dialog
            open={openTransformedPhotoDeleteDialog}
            onClose={() => setOpenTransformedPhotoDeleteDialog(false)}
            aria-labelledby='delete-dialog-title'
            aria-describedby='delete-dialog-description'
            PaperProps={{
               sx: {
                  bgcolor: INK2,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
               },
            }}
         >
            <DialogTitle id='delete-dialog-title' sx={{ color: '#fff' }}>
               Delete Photo?
            </DialogTitle>
            <DialogContent>
               <Typography id='delete-dialog-description' sx={{ color: W60 }}>
                  Are you sure you want to delete this photo? This action cannot be undone.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={handleDeleteCancel}
                  sx={{ color: W60 }}
               >
                  Cancel
               </Button>
               <Button onClick={handleDeleteTransformedPhotoConfirm} color='error' autoFocus>
                  Delete Photo
               </Button>
            </DialogActions>
         </Dialog>

         {/* Add photo to event album (from gallery popup) */}
         <Dialog
            open={addToAlbumDialogOpen}
            onClose={handleCloseAddToAlbumDialog}
            maxWidth='sm'
            fullWidth
            PaperProps={{
               sx: {
                  bgcolor: INK2,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
               },
            }}
         >
            <DialogTitle sx={{ color: '#fff' }}>
               Add to event album
            </DialogTitle>
            <DialogContent>
               <Typography sx={{ color: W60, mb: 2 }}>
                  Choose an album to add this photo to.
               </Typography>
               {loadingEventAlbums ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                     <CircularProgress size={28} />
                  </Box>
               ) : eventAlbumsList.length === 0 ? (
                  <Typography sx={{ color: W60 }}>
                     No albums yet for this event. Create one from the Albums tab first.
                  </Typography>
               ) : (
                  <List dense disablePadding>
                     {eventAlbumsList.map((album) => (
                        <ListItem
                           key={album.id}
                           disablePadding
                           component='button'
                           onClick={() => setSelectedAlbumIdForAdd(album.id)}
                           sx={{
                              width: '100%',
                              textAlign: 'left',
                              cursor: 'pointer',
                              py: 1.5,
                              px: 2,
                              mb: 1,
                              border: '1px solid',
                              borderColor:
                                 selectedAlbumIdForAdd === album.id
                                    ? 'primary.main'
                                    : 'rgba(255,255,255,0.12)',
                              borderRadius: 1,
                              bgcolor:
                                 selectedAlbumIdForAdd === album.id
                                    ? 'rgba(0,196,167,0.08)'
                                    : 'transparent',
                           }}
                        >
                           <ListItemText
                              primary={album.title}
                              secondary={
                                 album.photoCount != null
                                    ? `${album.photoCount} photo${album.photoCount !== 1 ? 's' : ''}`
                                    : undefined
                              }
                              primaryTypographyProps={{ sx: { color: '#fff' } }}
                              secondaryTypographyProps={{ sx: { color: W60 } }}
                           />
                        </ListItem>
                     ))}
                  </List>
               )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
               <Button onClick={handleCloseAddToAlbumDialog} sx={{ color: W60 }}>
                  Cancel
               </Button>
               <Button
                  variant='contained'
                  color='primary'
                  disabled={
                     loadingEventAlbums ||
                     addingPhotoToAlbum ||
                     !selectedAlbumIdForAdd ||
                     eventAlbumsList.length === 0
                  }
                  onClick={handleAddPhotoToAlbumConfirm}
               >
                  {addingPhotoToAlbum ? (
                     <>
                        <CircularProgress size={18} sx={{ mr: 1 }} />
                        Adding…
                     </>
                  ) : (
                     'Add to album'
                  )}
               </Button>
            </DialogActions>
         </Dialog>

         <Dialog
            open={openAIVersion}
            onClose={() => setOpenAIVersion(false)}
            maxWidth='lg'
            fullWidth
         >
            <DialogTitle>AI Version</DialogTitle>
            <DialogContent dividers>
               <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                     <Typography variant='subtitle2' align='center' gutterBottom>
                        Original Photo
                     </Typography>
                     <Box
                        sx={{
                           background: `url(${selectedPhoto?.url || ''}) `,
                           backgroundSize: 'cover',
                           backgroundPosition: 'center',
                           margin: 'auto',
                           width: '100%',
                           height: 400,
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           border: '1px solid #eee',
                           borderRadius: 1,
                           padding: 0,
                        }}
                     ></Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                     <Typography variant='subtitle2' align='center' gutterBottom>
                        AI Version
                     </Typography>

                     {eventTransformedPhotos.length == 0 ? (
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                           }}
                        >
                           <Typography variant='subtitle2' align='center' gutterBottom>
                              No AI Version
                           </Typography>
                        </Box>
                     ) : loadingTransformedPhotos ? (
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                           }}
                        >
                           <CircularProgress />
                        </Box>
                     ) : (
                        <Carousel
                           indicators={false}
                           animation='slide'
                           navButtonsAlwaysVisible={true}
                           navButtonsProps={{
                              style: {
                                 backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                 borderRadius: '50%',
                                 padding: '10px',
                                 color: 'white',
                              },
                           }}
                           NextIcon={<ArrowForwardIos />}
                           PrevIcon={<ArrowBackIos />}
                           sx={{
                              width: '100%',
                              height: '400px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              '& .MuiPaper-root': {
                                 height: '100%',
                              },
                           }}
                        >
                           {eventTransformedPhotos?.map((photo: any) => (
                              <>
                                 <Box
                                    key={photo.id}
                                    sx={{
                                       background: `url(${photo?.url})  `,
                                       backgroundSize: 'cover',
                                       backgroundPosition: 'center',
                                       position: 'relative!important',
                                       margin: 'auto',
                                       width: '100%',
                                       height: '400px',
                                       display: 'flex',
                                       justifyContent: 'center',
                                       alignItems: 'center',
                                       padding: 0,
                                    }}
                                 >
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          gap: 1,
                                          top: 10,
                                          left: '50%',
                                          zIndex: 50,
                                          position: 'absolute',
                                          alignItems: 'center',
                                       }}
                                    >
                                       <Tooltip title='Delete AI Version'>
                                          <IconButton
                                             sx={{
                                                width: '20px',
                                                height: '20px',
                                                backgroundColor: 'green',
                                                borderRadius: '50%',
                                                '&:hover': {
                                                   backgroundColor: 'green!important',
                                                },
                                             }}
                                             onClick={() => {
                                                handleTransformedPhotoDelete(photo);
                                             }}
                                          >
                                             <DeleteIcon
                                                sx={{
                                                   color: 'white',
                                                   width: '15px',
                                                   height: '15px',
                                                }}
                                                fontSize='small'
                                             />
                                          </IconButton>
                                       </Tooltip>
                                    </Box>
                                 </Box>
                              </>
                           ))}
                        </Carousel>
                     )}
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setOpenAIVersion(false)}>Close</Button>
            </DialogActions>
         </Dialog>

         {/* View All Photos Modal */}
         <Dialog
            open={viewAllPhotosOpen}
            onClose={handleCloseViewAllPhotos}
            maxWidth='xl'
            fullWidth
            PaperProps={{
               sx: {
                  height: '90vh',
                  maxHeight: '90vh',
               },
            }}
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='h5'>All Event Photos ({eventPhotos.length})</Typography>
                  <IconButton onClick={handleCloseViewAllPhotos}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 2, overflow: 'auto' }}>
               {loadingPhotos ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                     <CircularProgress />
                  </Box>
               ) : (
                  <Grid container spacing={2}>
                     {currentPhotos.map((photo: any) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={photo.id}>
                           <Card
                              sx={{
                                 position: 'relative',
                                 transition: 'transform 0.2s ease-in-out',
                                 border: selectedPhotos.has(photo.id)
                                    ? '3px solid #1976d2'
                                    : 'none',
                                 '&:hover': {
                                    transform: 'scale(1.02)',
                                    boxShadow: 3,
                                 },
                              }}
                           >
                              <Box sx={{ position: 'relative', width: '100%' }}>
                                 <CardMedia
                                    component='img'
                                    height='140'
                                    image={
                                       photo.mediaType === 'video'
                                          ? (photo.thumbnailUrl || photo.url)
                                          : photo.url
                                    }
                                    alt={photo.title}
                                    sx={{
                                       objectFit: 'cover',
                                       cursor: 'pointer',
                                       opacity: selectedPhotos.has(photo.id) ? 0.8 : 1,
                                    }}
                                    onClick={() =>
                                       window.open(
                                          photo.mediaType === 'video'
                                             ? (photo.videoUrl || photo.url)
                                             : photo.url,
                                          '_blank',
                                       )
                                    }
                                 />
                                 {photo.mediaType === 'video' && (
                                    <VideoMediaBadge iconSize={40} zIndex={1} />
                                 )}
                                 {hasUploaderMediaIds(photo.userId, photo.uploadedBy) &&
                                    (
                                       <MediaUploaderAttribution
                                          userId={photo.userId || photo.uploadedBy}
                                          onClick={() => {
                                             const uid = photo.userId || photo.uploadedBy;
                                             if (uid) setProfilePopoverUserId(uid);
                                          }}
                                          avatarSize={26}
                                          overlaySx={{ bottom: 4, left: 4, zIndex: 4 }}
                                       />
                                    )}
                              </Box>

                              {/* Selection checkbox - only visible for editors/owners */}
                              {canShare && (
                                 <Box
                                    position='absolute'
                                    top={8}
                                    left={8}
                                    bgcolor={
                                       selectedPhotos.has(photo.id) ? '#1976d2' : 'rgba(0,0,0,0.6)'
                                    }
                                    borderRadius='50%'
                                    p={0.3}
                                    sx={{
                                       transition: 'all 0.2s ease',
                                       border: selectedPhotos.has(photo.id)
                                          ? '2px solid white'
                                          : '2px solid transparent',
                                    }}
                                 >
                                    <Checkbox
                                       checked={selectedPhotos.has(photo.id)}
                                       onChange={(e) => {
                                          e.stopPropagation();
                                          togglePhotoSelection(photo.id);
                                       }}
                                       size='small'
                                       sx={{
                                          color: 'white',
                                          p: 0,
                                          '&.Mui-checked': {
                                             color: 'white',
                                          },
                                       }}
                                    />
                                 </Box>
                              )}

                              {/* Selection count badge */}
                              {selectedPhotos.has(photo.id) && (
                                 <Box
                                    position='absolute'
                                    top={8}
                                    left={8}
                                    bgcolor='#1976d2'
                                    color='white'
                                    borderRadius='50%'
                                    width={24}
                                    height={24}
                                    display='flex'
                                    alignItems='center'
                                    justifyContent='center'
                                    sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                                 >
                                    {Array.from(selectedPhotos).indexOf(photo.id) + 1}
                                 </Box>
                              )}

                              {(photo?.isTransformed || photo?.basePhotoId) && (
                                 <Typography
                                    sx={{
                                       position: 'absolute',
                                       bottom: 8,
                                       right: 8,
                                       backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                       color: 'white',
                                       padding: '4px 8px',
                                       borderRadius: '4px',
                                       fontSize: '0.75rem',
                                    }}
                                 >
                                    AI Generated
                                 </Typography>
                              )}

                              <CardContent sx={{ p: 2 }}>
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       justifyContent: 'space-between',
                                       alignItems: 'flex-start',
                                       mb: 1,
                                    }}
                                 >
                                    <Typography variant='subtitle2' noWrap sx={{ flex: 1, mr: 1 }}>
                                       {photo?.title || 'Untitled'}
                                    </Typography>
                                    {currentUser && (
                                       <PhotoLikeButton
                                          photoId={photo.id}
                                          userId={currentUser.uid}
                                          initialLikeCount={photo.likeCount || 0}
                                          initialIsLiked={photo.isLiked || false}
                                          size='small'
                                          variant='compact'
                                          showCount={true}
                                          eventId={eventId}
                                       />
                                    )}
                                 </Box>
                                 {photo.location && (
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          mt: 0.5,
                                       }}
                                    >
                                       <LocationOnIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                       {photo.location?.name}
                                    </Typography>
                                 )}
                                 {photo.description && (
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{
                                          mt: 1,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                       }}
                                    >
                                       {photo.description}
                                    </Typography>
                                 )}
                              </CardContent>
                           </Card>
                        </Grid>
                     ))}
                  </Grid>
               )}
            </DialogContent>

            <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 2 }}>
               {/* Pagination Controls */}
               {totalPages > 1 && (
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        width: '100%',
                     }}
                  >
                     <Button
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        startIcon={<ArrowBackIos />}
                        size='small'
                     >
                        Previous
                     </Button>

                     <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => handlePageChange(page)}
                        color='primary'
                        size='large'
                        showFirstButton
                        showLastButton
                     />

                     <Button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        endIcon={<ArrowForwardIos />}
                        size='small'
                     >
                        Next
                     </Button>
                  </Box>
               )}

               {/* Page Info and Close Button */}
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     width: '100%',
                  }}
               >
                  <Typography variant='body2' color='text.secondary'>
                     Showing {startIndex + 1}-{Math.min(endIndex, eventPhotos.length)} of{' '}
                     {eventPhotos.length} photos
                  </Typography>
                  <Button onClick={handleCloseViewAllPhotos} variant='outlined'>
                     Close
                  </Button>
               </Box>
            </DialogActions>
         </Dialog>

         {/* Edit Participant Dialog */}
         <Dialog open={editParticipantDialog} onClose={() => setEditParticipantDialog(false)}>
            <DialogTitle>Edit Participant Role</DialogTitle>
            <DialogContent>
               <DialogContentText sx={{ mb: 2 }}>
                  Change the role for {participantToEdit?.displayName || participantToEdit?.email}
               </DialogContentText>
               <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id='edit-role-label'>Role</InputLabel>
                  <Select
                     labelId='edit-role-label'
                     value={newRole}
                     label='Role'
                     onChange={(e) => setNewRole(e.target.value as 'owner' | 'editor' | 'viewer')}
                  >
                     <MenuItem value='viewer'>Viewer </MenuItem>
                     <MenuItem value='editor'>Editor </MenuItem>
                     <MenuItem value='owner'>Owner </MenuItem>
                  </Select>
               </FormControl>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setEditParticipantDialog(false)}>Cancel</Button>
               <Button onClick={confirmRoleChange} variant='contained'>
                  Save Changes
               </Button>
            </DialogActions>
         </Dialog>

         {/* Delete Participant Dialog */}
         <Dialog open={deleteParticipantDialog} onClose={() => setDeleteParticipantDialog(false)}>
            <DialogTitle>Remove Participant</DialogTitle>
            <DialogContent>
               <Typography variant='body1'>
                  Are you sure you want to remove{' '}
                  {participantToDelete?.displayName || participantToDelete?.email} from this event?
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  This action cannot be undone.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setDeleteParticipantDialog(false)}>Cancel</Button>
               <Button onClick={confirmParticipantDeletion} color='error' variant='contained'>
                  Remove
               </Button>
            </DialogActions>
         </Dialog>

         {/* Success/Error messages */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>

         {/* Participant Profile Modal */}
         <ParticipantProfileModal
            open={participantProfileModalOpen}
            onClose={handleCloseParticipantProfile}
            participant={selectedParticipant as any}
            currentUserId={currentUser?.uid}
            onAddToNetwork={
               selectedParticipant
                  ? () => addToContacts(selectedParticipant as EventParticipant)
                  : undefined
            }
         />

         {/* Event Flyer Generator Dialog */}
         {event && event.id && (
            <EventFlyerGenerator
               open={flyerDialogOpen}
               onClose={() => setFlyerDialogOpen(false)}
               event={event as Event}
               organizerProfile={
                  (() => {
                     const owner = (event.members || []).find((p) => p.role === 'owner');
                     const pd = owner?.profileData as Record<string, unknown> | undefined;
                     if (!owner && !event.userId) return null;
                     return {
                        name:
                           (pd?.displayName as string) ||
                           (pd?.name as string) ||
                           (pd?.fullName as string) ||
                           owner?.displayName ||
                           'Event Host',
                        photoURL: (pd?.photoURL as string) || (pd?.profilePicture as string) || (pd?.avatar as string) || undefined,
                        profilePicture: pd?.profilePicture as string | undefined,
                        avatar: pd?.avatar as string | undefined,
                        tagline: pd?.tagline as string | undefined,
                        bio: pd?.bio as string | undefined,
                     };
                  })()
               }
            />
         )}

         {/* Participant Reminder Modal */}
         <ParticipantReminderModal
            open={reminderModalOpen}
            onClose={handleCloseReminderModal}
            photos={eventPhotos.map((p) => ({
               id: p.id,
               url: p.url,
               title: p.title || 'Untitled',
            }))}
            selectedPhotos={selectedPhotosForReminder}
            onTogglePhoto={handleTogglePhotoForReminder}
            message={reminderMessage}
            onMessageChange={setReminderMessage}
            onSend={handleSendReminder}
            sending={sendingReminder}
            members={(event?.members || event?.participants || []).map((p: EventParticipant) => ({
               id: p.userId || p.email,
               email: p.email,
               displayName: p.displayName,
               name: p.displayName,
               profileData: p.profileData,
            }))}
            loadingPhotos={loadingPhotos}
            hasMorePhotos={false}
         />

         {/* Inline profile popover (uploader/photographer profile on avatar click) */}
         <Dialog
            open={!!profilePopoverUserId}
            onClose={() => {
               setProfilePopoverUserId(null);
               setProfilePopoverMessageMode(false);
               setProfilePopoverMessageText('');
               setProfilePopoverSenderEmail('');
               setProfilePopoverSenderName('');
               setProfilePopoverMessageError(null);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
               sx: {
                  borderRadius: 2,
                  overflow: 'hidden',
                  background: 'linear-gradient(165deg, #252530 0%, #1a1b22 50%, #16161a 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 24px 48px rgba(0,0,0,0.32), 0 0 0 1px rgba(255,255,255,0.03) inset',
               },
            }}
         >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 1.5, px: 2.5, borderBottom: '1px solid rgba(0,196,167,0.15)' }}>
               <IconButton
                  onClick={() => setProfilePopoverUserId(null)}
                  sx={{ color: 'rgba(0,196,167,0.8)', '&:hover': { color: '#00C4A7', bgcolor: 'rgba(0,196,167,0.1)' } }}
                  size="small"
               >
                  <CloseIcon fontSize="small" />
               </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5, pb: 2.5, px: 2.5, maxHeight: '75vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
               {profilePopoverLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                     <CircularProgress sx={{ color: '#00C4A7' }} size={28} />
                  </Box>
               ) : profilePopoverData?.photographerProfile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                     {profilePopoverMessageMode ? (
                        <>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <Button
                                 startIcon={<BackIconMsg />}
                                 size="small"
                                 onClick={() => {
                                    setProfilePopoverMessageMode(false);
                                    setProfilePopoverMessageError(null);
                                 }}
                                 sx={{ color: '#00C4A7', textTransform: 'none' }}
                              >
                                 Back
                              </Button>
                           </Box>
                           <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 1.5 }}>
                              Send a message to {profilePopoverData.photographerProfile.name || 'the photographer'}
                           </Typography>
                           <TextField
                              fullWidth
                              multiline
                              rows={4}
                              placeholder="Your message..."
                              value={profilePopoverMessageText}
                              onChange={(e) => setProfilePopoverMessageText(e.target.value)}
                              disabled={profilePopoverSending}
                              size="small"
                              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' } }}
                           />
                           {!currentUser && (
                              <>
                                 <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    label="Your email (so they can reply)"
                                    value={profilePopoverSenderEmail}
                                    onChange={(e) => setProfilePopoverSenderEmail(e.target.value)}
                                    disabled={profilePopoverSending}
                                    size="small"
                                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' } }}
                                 />
                                 <TextField
                                    fullWidth
                                    label="Your name (optional)"
                                    value={profilePopoverSenderName}
                                    onChange={(e) => setProfilePopoverSenderName(e.target.value)}
                                    disabled={profilePopoverSending}
                                    size="small"
                                    sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' } }}
                                 />
                              </>
                           )}
                           {profilePopoverMessageError && (
                              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setProfilePopoverMessageError(null)}>
                                 {profilePopoverMessageError}
                              </Alert>
                           )}
                           <Button
                              variant="contained"
                              fullWidth
                              disabled={profilePopoverSending || !profilePopoverMessageText.trim() || (!currentUser && !profilePopoverSenderEmail.trim())}
                              startIcon={profilePopoverSending ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <SendIcon />}
                              onClick={async () => {
                                 if (!profilePopoverUserId || !profilePopoverMessageText.trim()) return;
                                 if (!currentUser && !profilePopoverSenderEmail.trim()) {
                                    setProfilePopoverMessageError('Please enter your email so the photographer can reply.');
                                    return;
                                 }
                                 setProfilePopoverMessageError(null);
                                 setProfilePopoverSending(true);
                                 try {
                                    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                                    if (currentUser) {
                                       const authHeaders = await getAuthHeaders();
                                       Object.assign(headers, authHeaders);
                                    }
                                    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/photographer-inquiry`, {
                                       method: 'POST',
                                       headers,
                                       body: JSON.stringify({
                                          photographerUserId: profilePopoverUserId,
                                          message: profilePopoverMessageText.trim(),
                                          albumId: eventId || '',
                                          albumTitle: event?.title ?? undefined,
                                          ...(profilePopoverSenderEmail.trim() && { senderEmail: profilePopoverSenderEmail.trim() }),
                                          ...(profilePopoverSenderName.trim() && { senderName: profilePopoverSenderName.trim() }),
                                       }),
                                    });
                                    const data = await res.json().catch(() => ({}));
                                    if (!res.ok) {
                                       setProfilePopoverMessageError(data.message || data.error || 'Failed to send message');
                                       return;
                                    }
                                    if (data.emailSent === false) {
                                       setSnackbarMessage(data.message || 'Message saved, but the notification email to the photographer could not be sent. They can still see it in their Inbox.');
                                       setSnackbarSeverity('warning');
                                    } else {
                                       setSnackbarMessage('Your message was sent. The photographer will see it in their Inbox.');
                                       setSnackbarSeverity('success');
                                    }
                                    setSnackbarOpen(true);
                                    setProfilePopoverMessageText('');
                                    setProfilePopoverSenderEmail('');
                                    setProfilePopoverSenderName('');
                                    setProfilePopoverMessageMode(false);
                                 } catch (err: any) {
                                    setProfilePopoverMessageError(err?.message || 'Something went wrong. Please try again.');
                                 } finally {
                                    setProfilePopoverSending(false);
                                 }
                              }}
                              sx={{ bgcolor: '#00C4A7', color: '#fff', '&:hover': { bgcolor: '#017A7F' }, textTransform: 'none' }}
                           >
                              {profilePopoverSending ? 'Sending…' : 'Send'}
                           </Button>
                        </>
                     ) : (
                        <>
                     <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0, pr: 0.5 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                        <Box sx={{ position: 'relative', flexShrink: 0 }}>
                           <Avatar
                              src={profilePopoverData.photographerProfile.profilePhotoUrl}
                              sx={{
                                 width: 72,
                                 height: 72,
                                 border: '1px solid rgba(0,196,167,0.4)',
                                 boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              }}
                           >
                              {profilePopoverData.photographerProfile.name?.charAt(0) || '?'}
                           </Avatar>
                           <Box sx={{ position: 'absolute', bottom: -2, right: -2 }}>
                              <PhotographerBadge variant="avatarOverlay" size="md" />
                           </Box>
                        </Box>
                        <Box>
                           <Typography variant="subtitle1" sx={{ color: '#00C4A7', fontWeight: 500, mb: 0.25 }}>
                              {profilePopoverData.photographerProfile.name || 'Photographer'}
                           </Typography>
                           <Typography variant="caption" sx={{ color: 'rgba(0,196,167,0.9)', display: 'block' }}>
                              Photographer
                           </Typography>
                           {profilePopoverData.photographerProfile.photographerType && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mt: 0.25, textTransform: 'capitalize' }}>
                                 {profilePopoverData.photographerProfile.photographerType}
                              </Typography>
                           )}
                           {profilePopoverData.photographerProfile.portfolioPhotoCount != null && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                 <PhotoLibraryIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }} />
                                 {profilePopoverData.photographerProfile.portfolioPhotoCount} portfolio photos
                              </Typography>
                           )}
                        </Box>
                     </Box>
                     {profilePopoverData.photographerProfile.companyPhotoUrl && (
                        <Box sx={{ mb: 2 }}>
                           <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mb: 0.75 }}>
                              Company / Studio
                           </Typography>
                           <Box
                              component="img"
                              src={profilePopoverData.photographerProfile.companyPhotoUrl}
                              alt="Company"
                              sx={{
                                 width: '100%',
                                 maxHeight: 180,
                                 objectFit: 'cover',
                                 borderRadius: 1.5,
                                 border: '1px solid rgba(255,255,255,0.08)',
                              }}
                           />
                        </Box>
                     )}
                     {profilePopoverData.photographerProfile.companyName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                           <BusinessIcon sx={{ color: '#00C4A7', fontSize: 16 }} />
                           <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
                              {profilePopoverData.photographerProfile.companyName}
                           </Typography>
                        </Box>
                     )}
                     {profilePopoverData.photographerProfile.description && (
                        <Box sx={{ mb: 2 }}>
                           <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, fontWeight: 400 }}>
                              {profilePopoverData.photographerProfile.description}
                           </Typography>
                        </Box>
                     )}
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {profilePopoverData.photographerProfile.contactNumber && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ color: '#00C4A7', fontSize: 16 }} />
                              <Typography
                                 component="a"
                                 href={`tel:${profilePopoverData.photographerProfile.contactNumber}`}
                                 variant="body2"
                                 sx={{ color: '#00C4A7', textDecoration: 'none', fontWeight: 400, '&:hover': { color: 'rgba(0,196,167,0.85)' } }}
                              >
                                 {profilePopoverData.photographerProfile.contactNumber}
                              </Typography>
                           </Box>
                        )}
                        {profilePopoverData.photographerProfile.website && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LanguageIcon sx={{ color: '#00C4A7', fontSize: 16 }} />
                              <Typography
                                 component="a"
                                 href={profilePopoverData.photographerProfile.website}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 variant="body2"
                                 sx={{ color: '#00C4A7', fontWeight: 400, '&:hover': { color: 'rgba(0,196,167,0.85)' } }}
                              >
                                 {profilePopoverData.photographerProfile.website}
                              </Typography>
                           </Box>
                        )}
                     </Box>
                     </Box>
                     <Box sx={{ flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.08)', pt: 2, mt: 2 }}>
                        <Button
                           variant="outlined"
                           fullWidth
                           startIcon={<MessageIcon />}
                           onClick={() => setProfilePopoverMessageMode(true)}
                           sx={{
                              borderColor: '#00C4A7',
                              color: '#00C4A7',
                              textTransform: 'none',
                              '&:hover': { borderColor: '#017A7F', bgcolor: 'rgba(0,196,167,0.08)' },
                           }}
                        >
                           Message
                        </Button>
                     </Box>
                        </>
                     )}
                  </Box>
               ) : profilePopoverUserId && !profilePopoverLoading ? (
                  <Box sx={{ py: 2.5, textAlign: 'center' }}>
                     <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 400 }}>
                        Photographer profile not available for view.
                     </Typography>
                  </Box>
               ) : null}
            </DialogContent>
         </Dialog>

         {/* Photo Gallery Popup */}
         <PhotoGalleryPopup
            open={photoGalleryOpen}
            onClose={() => setPhotoGalleryOpen(false)}
            photos={(eventPhotos || []).map((photo) => ({
               ...photo,
               filename: (photo as any).filename || photo.title || 'Unknown',
               thumbnailUrl: (photo as any).thumbnailUrl || photo.url,
               title: photo.title || 'Untitled',
               description: photo.description || '',
               tags: photo.tags || [],
               mediaType: (photo as any).mediaType,
               videoUrl: (photo as any).videoUrl,
               userId: (photo as any).userId,
               uploadedBy: (photo as any).uploadedBy,
               takenAt:
                  photo.takenAt instanceof Date
                     ? photo.takenAt
                     : (photo.takenAt as any)?.toDate
                       ? (photo.takenAt as any).toDate()
                       : new Date(),
               uploadedAt:
                  photo.uploadedAt instanceof Date
                     ? photo.uploadedAt
                     : (photo.uploadedAt as any)?.toDate
                       ? (photo.uploadedAt as any).toDate()
                       : new Date(),
            }))}
            initialPhotoIndex={photoGalleryIndex}
            onAddToAlbum={
               (canShare || isEventOwner)
                  ? (photo) => handleOpenAddToAlbum(photo)
                  : undefined
            }
            onUploaderClick={(photo) => {
               const uid = (photo as any).userId || (photo as any).uploadedBy;
               if (uid) setProfilePopoverUserId(uid);
            }}
         />
      </Container>
   );
};

export default EventDetailPage;
